(() => {
  // app/scripts/utils/addEvent.js
  function addEvent(evt, selector, handler) {
    evt.split(" ").forEach((event) => {
      document.addEventListener(
        event,
        function(event2) {
          if (event2.target.matches(selector + ", " + selector + " *")) {
            handler.apply(event2.target.closest(selector), arguments);
          }
        },
        false
      );
    });
  }

  // app/scripts/utils/debounceTime.js
  function debounceTime(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // app/scripts/utils/resizeObserver.js
  function resizeObserver(selector, callback, debounceTimeout = 300) {
    const { debounceTime: debounceTime3 } = theme;
    let lastWidth = selector.clientWidth;
    const debounce = debounceTime3((e) => {
      let newestWidth = selector.clientWidth;
      if (newestWidth == lastWidth)
        return;
      callback.apply(selector);
      lastWidth = newestWidth;
    }, debounceTimeout);
    if ("ResizeObserver" in window) {
      const resizeObserver2 = new ResizeObserver((entries) => {
        debounce.call(selector);
      });
      resizeObserver2.observe(selector);
    } else {
      window.addEventListener("resize", () => debounce.bind(selector));
    }
  }

  // app/scripts/utils/inViewPort.js
  function inViewPort(el, callback, once = false) {
    if (!el || typeof callback !== "function")
      return;
    const options = {
      root: null,
      // viewport
      rootMargin: "0px",
      threshold: 0
      // trigger when element first appears in viewport
    };
    const observer = new IntersectionObserver((entries) => {
      const isIntersecting = entries[0].isIntersecting;
      callback(isIntersecting, entries);
      if (isIntersecting && once) {
        observer.disconnect();
      }
    }, options);
    observer.observe(el);
    return () => observer.disconnect();
  }

  // app/scripts/utils/checkAndUpdateQuantity.js
  function checkAndUpdateQuantity(data, quantity) {
    const newData = JSON.parse(JSON.stringify(data));
    newData.quantity = quantity;
    delete newData.properties._quantity;
    return newData;
  }

  // app/scripts/utils/dispatchEvent.js
  function dispatchEvent(event, options, elm) {
    return elm ? elm.dispatchEvent(event, options) : document.dispatchEvent(event, options);
  }

  // app/scripts/utils/disableScroll.js
  function disableScroll() {
    document.body.classList.add("overflow-hidden");
    document.documentElement.style.paddingRight = "var(--scrollbar-width)";
  }

  // app/scripts/utils/enableScroll.js
  function enableScroll() {
    setTimeout(() => {
      document.body.classList.remove("overflow-hidden");
      document.documentElement.style.paddingRight = "";
    }, 50);
  }

  // app/scripts/modules/header-messages.js
  var SELECTORS = {
    root: "[data-header-messages]",
    action: "[data-header-messages-action]",
    badge: "[data-header-messages-badge]",
    countText: "[data-header-messages-count-text]",
    countLabel: "[data-header-messages-count-label]",
    status: "[data-header-messages-status]",
    list: "[data-header-messages-list]",
    template: "[data-header-message-template]",
    messageLink: "[data-header-message-link]",
    messageTitle: "[data-header-message-title]",
    messageExcerpt: "[data-header-message-excerpt]",
    messagePublishedAt: "[data-header-message-published-at]",
    markAll: "[data-header-messages-mark-all]",
    seeAll: "[data-header-messages-see-all]",
    footerRule: "[data-header-messages-footer-rule]"
  };
  function toStringValue(value) {
    return typeof value === "string" ? value : "";
  }
  function toBooleanValue(value) {
    return value === true;
  }
  function toUrlValue(value, fallback = "#") {
    const url = toStringValue(value).trim();
    return url || fallback;
  }
  function toIntegerValue(value) {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.trunc(number)) : null;
  }
  function normalizeMessage(message) {
    if (!message || typeof message !== "object")
      return null;
    const id = toStringValue(message.id).trim();
    const title = toStringValue(message.title).trim();
    if (!id || !title)
      return null;
    return {
      id,
      title,
      excerpt: toStringValue(message.excerpt).trim(),
      url: toUrlValue(message.url),
      read: toBooleanValue(message.read),
      publishedAt: toStringValue(message.publishedAt).trim()
    };
  }
  function normalizeHeaderMessagesPayload(payload = {}) {
    const messages = Array.isArray(payload.messages) ? payload.messages.map(normalizeMessage).filter(Boolean) : [];
    const explicitUnreadCount = toIntegerValue(payload.unreadCount);
    return {
      enabled: payload.enabled !== false,
      unreadCount: explicitUnreadCount ?? messages.filter((message) => !message.read).length,
      seeAllUrl: toUrlValue(payload.seeAllUrl),
      markAllAsReadUrl: toUrlValue(payload.markAllAsReadUrl),
      messages
    };
  }
  function setHidden(element, hidden) {
    if (!element)
      return;
    element.classList.toggle("hidden", hidden);
  }
  function setTextContent(element, text, hideWhenBlank = false) {
    if (!element)
      return;
    element.textContent = text;
    if (hideWhenBlank)
      setHidden(element, !text);
  }
  function setCountText(element, count) {
    if (!element)
      return;
    element.textContent = count > 0 ? `(${count})` : "";
  }
  function setCountLabel(element, count) {
    if (!element)
      return;
    element.textContent = `(${count})`;
  }
  function getSurfaces() {
    return [document, document.getElementById("mobileMenuTemplate")?.content].filter(Boolean);
  }
  function syncMessageCount(count, label) {
    getSurfaces().forEach((surface) => {
      surface.querySelectorAll(SELECTORS.badge).forEach((badge) => setHidden(badge, count === 0));
      surface.querySelectorAll(SELECTORS.countText).forEach((countText) => setCountText(countText, count));
      surface.querySelectorAll(SELECTORS.countLabel).forEach((countLabel) => setCountLabel(countLabel, count));
      surface.querySelectorAll(SELECTORS.action).forEach((action) => {
        if (action.hasAttribute("aria-label")) {
          action.setAttribute("aria-label", count > 0 ? `${label} (${count})` : label);
        }
      });
    });
  }
  function setMessageState(item, read) {
    item.classList.toggle("is-read", read);
    item.classList.toggle("is-unread", !read);
  }
  function createMessageItem(message, template) {
    const item = template.content.firstElementChild?.cloneNode(true);
    if (!item)
      return null;
    const link = item.matches(SELECTORS.messageLink) ? item : item.querySelector(SELECTORS.messageLink);
    if (link)
      link.href = message.url;
    setTextContent(item.querySelector(SELECTORS.messageTitle), message.title);
    setTextContent(item.querySelector(SELECTORS.messageExcerpt), message.excerpt, true);
    setTextContent(item.querySelector(SELECTORS.messagePublishedAt), message.publishedAt, true);
    setMessageState(item, message.read);
    return item;
  }
  function renderMessages(root, state) {
    const status = root.querySelector(SELECTORS.status);
    const list = root.querySelector(SELECTORS.list);
    const template = root.querySelector(SELECTORS.template);
    const markAll = root.querySelector(SELECTORS.markAll);
    const seeAll = root.querySelector(SELECTORS.seeAll);
    const footerRule = root.querySelector(SELECTORS.footerRule);
    if (markAll)
      markAll.href = state.markAllAsReadUrl;
    if (seeAll)
      seeAll.href = state.seeAllUrl;
    if (!list)
      return;
    if (!template)
      throw new Error("Header messages template is missing");
    const messageItems = state.messages.map((message) => createMessageItem(message, template)).filter(Boolean);
    list.replaceChildren(...messageItems);
    const hasMessages = state.enabled && messageItems.length > 0;
    setHidden(status, hasMessages);
    setHidden(list, !hasMessages);
    setHidden(markAll, !hasMessages);
    setHidden(seeAll, !hasMessages);
    setHidden(footerRule, !hasMessages);
    if (status && !hasMessages) {
      status.textContent = root.dataset.headerMessagesEmpty || "";
    }
  }
  function renderError(root) {
    const status = root.querySelector(SELECTORS.status);
    const list = root.querySelector(SELECTORS.list);
    const markAll = root.querySelector(SELECTORS.markAll);
    const seeAll = root.querySelector(SELECTORS.seeAll);
    const footerRule = root.querySelector(SELECTORS.footerRule);
    setHidden(status, false);
    setHidden(list, true);
    setHidden(markAll, true);
    setHidden(seeAll, true);
    setHidden(footerRule, true);
    if (status) {
      status.textContent = root.dataset.headerMessagesError || "";
    }
  }
  async function initHeaderMessages(container = document) {
    const root = container.querySelector(SELECTORS.root);
    if (!root)
      return;
    const endpoint = root.dataset.headerMessagesEndpoint;
    const label = root.dataset.headerMessagesLabel || "Messages";
    if (!endpoint) {
      syncMessageCount(0, label);
      renderError(root);
      return;
    }
    try {
      const response = await fetch(endpoint, { headers: { Accept: "application/json" } });
      if (!response.ok)
        throw new Error(`Header messages request failed: ${response.status}`);
      const state = normalizeHeaderMessagesPayload(await response.json());
      const visibleCount = state.enabled ? state.unreadCount : 0;
      syncMessageCount(visibleCount, label);
      renderMessages(root, state);
    } catch (error) {
      syncMessageCount(0, label);
      renderError(root);
      console.warn(error);
    }
  }

  // app/scripts/sections/header.js
  var SCROLL_THRESHOLD = 50;
  var HEADROOM_SCROLL_DELTA = 5;
  var MOBILE_MENU_CLOSE_DELAY = 350;
  var HELP_POPUP_DISMISSED_PREFIX = "rsl:help-popup-dismissed:";
  var section = {
    type: "header",
    onLoad() {
    },
    onSelect() {
    },
    onDeselect(evt) {
    },
    onBlockSelect(e) {
    },
    onBlockDeselect(e) {
    },
    syncHeaderButtonExpanded(button) {
      if (!button?.hasAttribute("aria-expanded"))
        return;
      const isExpanded = button.classList.contains("show") || button.parentElement?.classList.contains("show");
      button.setAttribute("aria-expanded", isExpanded ? "true" : "false");
    },
    hideHeaderButton(button) {
      button.classList.remove("show");
      this.syncHeaderButtonExpanded(button);
    },
    toggleHeaderButton(button) {
      button.classList.toggle("show");
      this.syncHeaderButtonExpanded(button);
    },
    syncHeaderPopoverState() {
      const isOpen = Array.from(this.container.querySelectorAll(".js-header-icon")).some(
        (button) => button.parentElement?.classList.contains("show")
      );
      this.container.classList.toggle("is-header-popover-open", isOpen);
    },
    getHeaderPopoverTrigger(popoverName) {
      if (popoverName === "cart")
        return this.container.querySelector("header-cart a");
      return this.container.querySelector(`[data-header-popover="${popoverName}"]`);
    },
    // The fulfilment dropdown is lazy-hydrated and often not upgraded yet, so sync off the document:
    // server-rendered radios + flipping :checked drive the header via :has() regardless.
    syncDeliveryMethodRadios(event) {
      const method = event.detail?.deliveryMethod;
      if (!method)
        return;
      const input = document.querySelector(`input.delivery-method[value='${method}']`);
      if (input && !input.checked)
        input.checked = true;
    },
    onMegaMenuResize() {
      const menu = this.container.querySelector('[data-section-type="mega-menu"]');
      const header = document.getElementById("theme-header");
      if (!menu || !header)
        return;
      const defaultMenuHeight = parseInt(menu.dataset.menuHeight, 10) || 620;
      const headerHeight = header.offsetHeight;
      const viewportHeight = window.innerHeight;
      let newHeight = defaultMenuHeight;
      const availableHeight = viewportHeight - headerHeight - 100;
      if (availableHeight < defaultMenuHeight) {
        newHeight = Math.max(availableHeight, 320);
      }
      menu.style.setProperty("--height", `${newHeight}px`);
    },
    init() {
      this.container = document.querySelector(`[data-section-type="${this.type}"]`);
      if (!this.container)
        return;
      this.cart = this.container?.querySelector("header-cart");
      const mobileSearchMode = this.container.dataset.visibility;
      const themeHeader = this.themeHeader = document.getElementById("theme-header");
      themeHeader.addEventListener("click", (e) => {
        const { target } = e;
        const button = target.closest("button");
        if (button) {
          themeHeader.querySelectorAll("button.show").forEach((btn) => {
            button != btn && this.hideHeaderButton(btn);
          });
          this.toggleHeaderButton(button);
        }
        const mainNav = target.closest(".toggle-button");
        const currentMegaMenu = mainNav?.closest(".toggle-mega-menu");
        const megaMenu = target.closest("mega-menu");
        const dropdownMegaMenu = target.closest(".dropdown-mega-menu");
        if (mainNav) {
          e.stopPropagation();
          if (!(megaMenu || dropdownMegaMenu)) {
            if (currentMegaMenu?.querySelector("mega-menu")) {
              e.preventDefault();
            }
            document.querySelectorAll(".toggle-mega-menu").forEach((menu) => {
              if (menu !== currentMegaMenu) {
                menu.classList.remove("show");
                themeHeader.querySelectorAll("button.show").forEach((btn) => {
                  if (button !== btn)
                    this.hideHeaderButton(btn);
                });
              }
            });
            currentMegaMenu.classList.toggle("show");
          }
        } else {
          if (!(megaMenu || dropdownMegaMenu)) {
            document.querySelectorAll(".toggle-mega-menu").forEach((menu) => {
              menu.classList.remove("show");
            });
            themeHeader.querySelectorAll("button.show").forEach((btn) => {
              if (btn !== button)
                this.hideHeaderButton(btn);
            });
          }
        }
        document.addEventListener("click", (e2) => {
          const target2 = e2.target;
          const isInsideHeader = themeHeader.contains(target2);
          const isMegaMenu = target2.closest("mega-menu");
          const isDropdown = target2.closest(".dropdown-mega-menu");
          if (!isInsideHeader && !isMegaMenu && !isDropdown) {
            document.querySelectorAll(".toggle-mega-menu").forEach((menu) => {
              menu.classList.remove("show");
            });
            themeHeader.querySelectorAll("button.show").forEach((btn) => {
              this.hideHeaderButton(btn);
            });
          }
        });
      });
      this.container.querySelectorAll(".jsNavMega").forEach((a) => {
        a.addEventListener("mouseenter", this.onNavHover.bind(this, a), { once: true });
        a.addEventListener("focusin", (e) => {
          a.closest("li").classList.add("is-focusing");
          this.onNavHover(a);
        });
        a.addEventListener("focusout", (e) => {
          setTimeout(() => {
            !a.closest("li").querySelectorAll(".is-focusing").length && a.closest("li").classList.remove("is-focusing");
          }, 100);
        });
      });
      this.onMegaMenuResize();
      window.addEventListener("resize", this.debounce(this.onMegaMenuResize.bind(this), 250));
      this.initHeaderIcons();
      this.initHelpPopups();
      this.initMobileHeaderPopoverTriggers();
      window.addEventListener("deliveryMethod:change", this.syncDeliveryMethodRadios);
      initHeaderMessages(this.container);
      mobileSearchMode == "minimize" && this.initScrollDetection();
      this.initHeadroom();
    },
    // Hide the announcement + secondary bar on scroll down, reveal them on scroll up.
    // The whole sticky header slides up by the aux-bar height (CSS transform on .is-condensed),
    // keeping the main bar pinned. Transform avoids the layout shift a collapsing sticky box
    // would cause, which otherwise fights browser scroll anchoring and oscillates.
    initHeadroom() {
      const header = this.themeHeader;
      if (!header)
        return;
      const container = this.container;
      const auxBars = header.querySelectorAll(".header-aux-collapse");
      let auxHeight = 0;
      let lastScrollY = window.scrollY;
      let ticking = false;
      const root = document.documentElement.style;
      const measure = () => {
        auxHeight = Array.from(auxBars).reduce((total, el) => total + el.offsetHeight, 0);
        const headerHeight = header.offsetHeight;
        const mainBarHeight = header.querySelector(".js-header-main-bar")?.offsetHeight;
        const searchFormHeight = header.querySelector(".js-search-form")?.offsetHeight;
        root.setProperty("--header-aux-height", `${auxHeight}px`);
        root.setProperty("--header-pinned-height", `${Math.max(headerHeight - auxHeight, 0)}px`);
        if (mainBarHeight !== void 0) {
          root.setProperty("--header-main-bar-height", `${mainBarHeight}px`);
        }
        if (searchFormHeight !== void 0) {
          root.setProperty("--header-bar-height", `${headerHeight - searchFormHeight}px`);
        }
      };
      const update = () => {
        const currentScrollY = window.scrollY;
        if (container?.classList.contains("is-header-popover-open")) {
          lastScrollY = currentScrollY;
          ticking = false;
          return;
        }
        if (currentScrollY <= auxHeight) {
          header.classList.remove("is-condensed");
        } else if (currentScrollY > lastScrollY + HEADROOM_SCROLL_DELTA) {
          header.classList.add("is-condensed");
        } else if (currentScrollY < lastScrollY - HEADROOM_SCROLL_DELTA) {
          header.classList.remove("is-condensed");
        }
        lastScrollY = currentScrollY;
        ticking = false;
      };
      measure();
      const mainBar = header.querySelector(".js-header-main-bar");
      if (mainBar && "ResizeObserver" in window) {
        new ResizeObserver(() => measure()).observe(mainBar);
      }
      window.addEventListener(
        "scroll",
        () => {
          if (!ticking) {
            window.requestAnimationFrame(update);
            ticking = true;
          }
        },
        { passive: true }
      );
      window.addEventListener(
        "resize",
        this.debounce(() => {
          header.classList.remove("is-condensed");
          measure();
          lastScrollY = window.scrollY;
        }, 250)
      );
    },
    initHeaderIcons() {
      const buttons = this.container.querySelectorAll(".js-header-icon");
      buttons.forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          buttons.forEach((btn) => {
            if (btn !== button) {
              btn.parentElement.classList.remove("show");
              this.syncHeaderButtonExpanded(btn);
            }
          });
          button.parentElement.classList.toggle("show", !button.parentElement.classList.contains("show"));
          this.syncHeaderButtonExpanded(button);
          this.syncHeaderPopoverState();
        });
      });
      document.addEventListener("click", (e) => {
        buttons.forEach((btn) => {
          if (!btn.parentElement.contains(e.target)) {
            btn.parentElement.classList.remove("show");
            this.syncHeaderButtonExpanded(btn);
            this.syncHeaderPopoverState();
          }
        });
      });
    },
    // The help popups open on hover/focus through CSS alone, so suppressing one means taking it out of
    // the flow entirely: [hidden] beats the group-hover visibility, and dropping data-popover-trigger
    // stops the header dimming overlay from firing for a popup that no longer shows.
    initHelpPopups() {
      this.container.querySelectorAll("[data-help-popup]").forEach((popup) => {
        const storageKey = `${HELP_POPUP_DISMISSED_PREFIX}${popup.dataset.helpPopup}`;
        const host = popup.closest("[data-popover-trigger]");
        const navLink = host?.querySelector("a");
        const hide = () => {
          popup.hidden = true;
          host?.removeAttribute("data-popover-trigger");
          navLink?.removeAttribute("aria-haspopup");
        };
        let isDismissed = false;
        try {
          isDismissed = localStorage.getItem(storageKey) === "1";
        } catch (error) {
        }
        if (isDismissed) {
          hide();
          return;
        }
        popup.querySelector("[data-help-popup-dismiss]")?.addEventListener("click", (event) => {
          event.stopPropagation();
          try {
            localStorage.setItem(storageKey, "1");
          } catch (error) {
          }
          hide();
          navLink?.focus({ preventScroll: true });
        });
      });
    },
    initMobileHeaderPopoverTriggers() {
      if (this.mobileHeaderPopoverTriggersInitialized)
        return;
      this.mobileHeaderPopoverTriggersInitialized = true;
      document.addEventListener("click", (event) => {
        const trigger = event.target.closest("#mobileMenu [data-mobile-header-popover]");
        if (!trigger)
          return;
        const headerTrigger = this.getHeaderPopoverTrigger(trigger.dataset.mobileHeaderPopover);
        if (!headerTrigger)
          return;
        event.preventDefault();
        event.stopPropagation();
        const openHeaderTrigger = () => {
          if (!document.contains(headerTrigger))
            return;
          headerTrigger.click();
          if (trigger.dataset.mobileHeaderPopover !== "cart") {
            headerTrigger.focus({ preventScroll: true });
          }
        };
        const mobileMenu = document.getElementById("mobileMenu");
        if (!mobileMenu?.isOpen || typeof mobileMenu.close !== "function") {
          openHeaderTrigger();
          return;
        }
        const drawerContent = mobileMenu.querySelector(".popup-content");
        let hasOpenedHeaderTrigger = false;
        const openOnce = () => {
          if (hasOpenedHeaderTrigger)
            return;
          hasOpenedHeaderTrigger = true;
          openHeaderTrigger();
        };
        drawerContent?.addEventListener("transitionend", openOnce, { once: true });
        window.setTimeout(openOnce, MOBILE_MENU_CLOSE_DELAY);
        mobileMenu.close();
      });
    },
    onNavHover(el) {
      const nav = el;
      const template = nav.parentNode.querySelector(".jsTemplate");
      if (!template)
        return;
      template.tagName == "TEMPLATE" && Array.from(template.content.children)?.forEach((child) => {
        template.insertAdjacentElement("beforebegin", child);
      });
      template.tagName == "NOSCRIPT" && template.insertAdjacentHTML("beforebegin", template.textContent);
      template.remove();
      const parentNode = nav.parentNode;
      parentNode.classList.add("has-dropdown");
      parentNode.classList.toggle("has-mega-menu", parentNode.querySelector('[data-section-type="mega-menu"]'));
      parentNode.querySelector(":scope > a ~ template")?.remove();
      parentNode.querySelectorAll("a").forEach((a) => {
        a.addEventListener("focusin", (e) => {
          a.classList.add("is-focusing");
        });
        a.addEventListener("focusout", (e) => {
          a.classList.remove("is-focusing");
          setTimeout(() => {
            !parentNode.querySelectorAll(".is-focusing").length && parentNode.classList.remove("is-focusing");
          }, 100);
        });
      });
      this.calHeight();
    },
    calHeight() {
      this.container.querySelectorAll(".jsCalHeight").forEach((item) => {
        item.style.setProperty("--menu-height", item.firstElementChild.scrollHeight + "px");
      });
    },
    // Scroll detection function
    initScrollDetection() {
      let lastScrollY = window.scrollY;
      let isScrollingDown = false;
      let firstScroll = true;
      const searchForm = this.themeHeader.querySelector("predictive-search");
      const iconSearch = this.themeHeader.querySelector(".js-icon-search");
      if (!searchForm || !iconSearch) {
        console.warn("Search form or icon search not found");
        return;
      }
      iconSearch.addEventListener("click", () => {
        this.themeHeader.classList.remove("scrolled-down");
        lastScrollY = window.scrollY;
        isScrollingDown = false;
      });
      window.addEventListener("scroll", () => {
        if (window.innerWidth > 990)
          return;
        const currentScrollY = window.scrollY;
        if (Math.abs(currentScrollY - lastScrollY) < SCROLL_THRESHOLD)
          return;
        if (firstScroll) {
          firstScroll = false;
          lastScrollY = currentScrollY;
          return;
        }
        if (currentScrollY > lastScrollY) {
          if (!isScrollingDown) {
            isScrollingDown = true;
            this.onScrollDown();
          }
        } else if (currentScrollY < lastScrollY) {
          if (isScrollingDown) {
            isScrollingDown = false;
            this.onScrollUp();
          }
        }
        lastScrollY = currentScrollY;
      });
    },
    onScrollDown() {
      this.themeHeader.classList.add("scrolled-down");
    },
    onScrollUp() {
      this.themeHeader.classList.remove("scrolled-down");
    },
    debounce(fn, delay2) {
      let timer;
      return function(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay2);
      };
    }
  };

  // app/scripts/utils/delay.js
  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // app/scripts/modules/drawer-management.js
  var DrawerManagement = class {
    constructor() {
      this.drawers = [...document.querySelectorAll(".drawer")].reduce((previousValue, drawer) => {
        const id = drawer.id;
        if (!previousValue[id]) {
          previousValue[id] = drawer;
        }
        return previousValue;
      }, {});
      this.cart = this.drawers["cart-drawer"];
    }
    openCart() {
      if (!this.cart)
        return false;
      this.cart.open();
      return true;
    }
    open(id) {
      this.drawers[id].open();
    }
    close(id) {
      this.drawers[id].close();
    }
  };

  // app/scripts/modules/toast.js
  var TOAST_DURATION = 5e3;
  var TOAST_DURATION_WITH_ACTION = 8e3;
  var Toast = class {
    get container() {
      if (!this._container)
        this._container = document.getElementById("toast-container");
      return this._container;
    }
    get template() {
      if (!this._template)
        this._template = document.getElementById("cart-toast-template");
      return this._template;
    }
    cartAdded(data) {
      const { container, template } = this;
      if (!container || !template)
        return;
      const productTitle = data?.product_title;
      const strings = theme.string?.cart;
      if (!productTitle && !strings?.itemAdded)
        return;
      this.clear();
      const toast = template.content.firstElementChild.cloneNode(true);
      this.renderTitle(toast.querySelector("[data-toast-title]"), productTitle, strings);
      this.bindViewCart(toast.querySelector("[data-toast-view-cart]"));
      toast.querySelector("[data-toast-close]")?.addEventListener("click", () => this.clear());
      container.appendChild(toast);
      this.timer = setTimeout(() => this.clear(), TOAST_DURATION);
    }
    show(message, { highlight, action, tone } = {}) {
      const container = this.container;
      const template = document.getElementById("toast-template");
      if (!container || !template || !message)
        return;
      this.clear();
      const toast = template.content.firstElementChild.cloneNode(true);
      if (tone === "error")
        toast.classList.add("toast--error");
      this.renderMessage(toast.querySelector("[data-toast-title]"), message, highlight);
      const hasAction = this.bindAction(toast.querySelector("[data-toast-action]"), action);
      toast.querySelector("[data-toast-close]")?.addEventListener("click", () => this.clear());
      const duration = hasAction ? TOAST_DURATION_WITH_ACTION : TOAST_DURATION;
      toast.style.setProperty("--toast-duration", `${duration}ms`);
      container.appendChild(toast);
      this.timer = setTimeout(() => this.clear(), duration);
    }
    // Wire up the optional action link; returns true when one was rendered.
    bindAction(link, action) {
      if (!link || !action?.href || !action?.label)
        return false;
      link.href = action.href;
      link.textContent = action.label;
      link.hidden = false;
      link.addEventListener("click", () => this.clear());
      return true;
    }
    renderMessage(el, message, highlight) {
      if (!el)
        return;
      el.replaceChildren();
      const start = highlight ? message.indexOf(highlight) : -1;
      if (start === -1) {
        el.textContent = message;
        return;
      }
      this.appendHighlighted(el, message.slice(0, start), highlight, message.slice(start + highlight.length));
    }
    // Append "<before><bold highlight><after>" into el, omitting empty segments.
    appendHighlighted(el, before, highlight, after) {
      const strong = document.createElement("span");
      strong.className = "text-body-m-bold";
      strong.textContent = highlight;
      if (before)
        el.append(before);
      el.append(strong);
      if (after)
        el.append(after);
    }
    renderTitle(el, productTitle, strings) {
      if (!el)
        return;
      el.replaceChildren();
      if (productTitle && strings?.addedToCart?.includes("{{product_title}}")) {
        const [before, after] = strings.addedToCart.split("{{product_title}}");
        this.appendHighlighted(el, before, productTitle, after);
        return;
      }
      el.textContent = strings?.itemAdded || productTitle || "";
    }
    bindViewCart(link) {
      if (!link)
        return;
      link.addEventListener("click", (event) => {
        if (theme.drawers?.openCart()) {
          event.preventDefault();
          this.clear();
        }
      });
    }
    clear() {
      this.clearTimer();
      this.container?.replaceChildren();
    }
    clearTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }
  };

  // app/scripts/utils/debounceEvent.js
  var debouncedEventDispatcher = /* @__PURE__ */ (() => {
    const timeouts = /* @__PURE__ */ new Map();
    const DEFAULT_DELAY = 300;
    return {
      dispatch(eventName, detail = {}, delay2 = DEFAULT_DELAY) {
        if (timeouts.has(eventName)) {
          clearTimeout(timeouts.get(eventName));
        }
        const timeout = setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent(eventName, { detail }));
          } catch (error) {
            console.error(`Error dispatching ${eventName}:`, error);
          }
          timeouts.delete(eventName);
        }, delay2);
        timeouts.set(eventName, timeout);
      },
      clear(eventName) {
        if (timeouts.has(eventName)) {
          clearTimeout(timeouts.get(eventName));
          timeouts.delete(eventName);
        }
      },
      clearAll() {
        timeouts.forEach((timeout) => clearTimeout(timeout));
        timeouts.clear();
      }
    };
  })();
  var debounceEvent_default = debouncedEventDispatcher;

  // app/scripts/utils/handleQueue.js
  async function handleQueue(callback) {
    try {
      await new Promise((resolve) => {
        requestAnimationFrame(async () => {
          await callback();
          resolve();
        });
      });
    } catch (error) {
      console.error(error);
    } finally {
    }
  }

  // app/scripts/utils/storage.js
  function localStorage2(storageKey, defaultValue = []) {
    const key = storageKey;
    const storage = {
      get: () => {
        try {
          const data = window.localStorage.getItem(key);
          return data ? JSON.parse(data) : defaultValue;
        } catch (error) {
          console.error(`Error getting item from localStorage: ${error.message}`);
          return defaultValue;
        }
      },
      set: (value, options = {}) => {
        try {
          const currentValue = storage.get();
          if (JSON.stringify(currentValue) === JSON.stringify(value)) {
            return;
          }
          const data = { value };
          if (options.expire) {
            data.expire = Date.now() + 24 * 60 * 60 * 1e3 * options.expire;
          }
          window.localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
          console.error(`Error setting item in localStorage: ${error.message}`);
          throw error;
        }
      },
      clear: () => {
        try {
          window.localStorage.removeItem(key);
        } catch (error) {
          console.error(`Error clearing item from localStorage: ${error.message}`);
          throw error;
        }
      },
      eventDispatch: () => {
        window.dispatchEvent(
          new CustomEvent(`localStorage:${key}:change`, {
            bubbles: true,
            detail: { key }
          })
        );
      }
    };
    return storage;
  }
  function sessionStorage(storageKey) {
    const key = storageKey;
    const sessionStore = window.sessionStorage;
    const storage = {
      get: () => {
        try {
          const data = sessionStore.getItem(key);
          return data ? JSON.parse(data) : null;
        } catch (error) {
          console.error(`Error getting item from sessionStorage: ${error.message}`);
          return null;
        }
      },
      set: (value) => {
        try {
          sessionStore.setItem(key, JSON.stringify(value));
        } catch (error) {
          console.error(`Error setting item in sessionStorage: ${error.message}`);
          throw error;
        }
      },
      clear: () => {
        try {
          sessionStore.removeItem(key);
        } catch (error) {
          console.error(`Error clearing item from sessionStorage: ${error.message}`);
          throw error;
        }
      }
    };
    return storage;
  }

  // app/scripts/utils/getVariantsInfo.js
  async function getVariantsInfo(variantIds) {
    try {
      const ids = [...new Set(variantIds).values()];
      const list = {};
      do {
        const res = await Promise.all(
          ids.splice(0, 20).map(
            (id) => fetch(`${Shopify.routes.root}variants/${id}?section_id=variant-info`).then(
              (res2) => res2.text().then((htmlString) => {
                const div = document.createElement("div");
                div.innerHTML = htmlString;
                return { id, value: JSON.parse(div.textContent) };
              })
            )
          )
        );
        res.forEach(({ id, value }) => {
          list[id] = value;
        });
      } while (ids.length > 0);
      return list;
    } catch (error) {
      throw error;
    }
  }

  // app/scripts/utils/distance.js
  var EARTH_RADIUS_KM = 6371;
  var degreesToRadians = (degrees) => degrees * Math.PI / 180;
  var haversineKm = (lat1, lng1, lat2, lng2) => {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null || isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2)) {
      return null;
    }
    const lat1Rad = degreesToRadians(Number(lat1));
    const lat2Rad = degreesToRadians(Number(lat2));
    const dLat = degreesToRadians(Number(lat2) - Number(lat1));
    const dLng = degreesToRadians(Number(lng2) - Number(lng1));
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return EARTH_RADIUS_KM * c;
  };

  // app/scripts/utils/getPcNumbersForLocation.js
  function getPcNumbersForLocation(_location) {
    return theme.shopLocations.filter((location2) => !!location2.pc_number).filter((location2) => location2.region === _location.region).map((location2) => {
      const {
        address: { latitude, longitude }
      } = location2;
      const distanceKm = haversineKm(
        Number(latitude),
        Number(longitude),
        Number(_location.address.latitude),
        Number(_location.address.longitude)
      );
      return { ...location2, distanceKm };
    }).sort((a, b) => a.distanceKm - b.distanceKm).filter(({ distanceKm }) => distanceKm <= theme.settings.distanceMax).map((location2) => location2.pc_number);
  }

  // app/scripts/utils/calculateRegionInventoryTotal.js
  function calculateRegionInventoryTotal(WarehousesQuantities, location2, locationInventoryQty) {
    const start = location2.pc_number === window.rsl_customer.WarehouseName ? locationInventoryQty : 0;
    const pcNumbers = getPcNumbersForLocation(location2);
    const newRegionInventories = WarehousesQuantities.filter((item) => pcNumbers.includes(item.WarehouseName));
    return newRegionInventories.reduce((sum, item) => sum + item.Quantity, start);
  }

  // app/scripts/utils/getPickupQuantity.js
  function getPickupQuantity(WarehousesQuantities, location2, locationInventoryQty) {
    if (location2.pc_number !== window.rsl_customer.WarehouseName) {
      return WarehousesQuantities.find(({ WarehouseName }) => WarehouseName === location2.pc_number)?.Quantity || 0;
    }
    return locationInventoryQty;
  }

  // app/scripts/utils/getLocalizedRegionName.js
  var localizedRegionMap = {};
  var locationMap = theme?.shopLocations?.reduce((acc, location2) => {
    if (location2.id) {
      acc[location2.id] = location2;
    }
    return acc;
  }, {}) || {};
  if (Shopify.locale !== "en") {
    Object.assign(
      localizedRegionMap,
      theme?.regionData?.reduce((acc, region) => {
        const foundLocation = locationMap[region.location_id];
        if (foundLocation && foundLocation.region) {
          acc[region.title] = foundLocation.region;
        }
        return acc;
      }, {})
    );
  }
  function getLocalizedRegionName(regionName) {
    return localizedRegionMap[regionName] || regionName;
  }

  // app/scripts/utils/index.js
  window.theme = Object.assign(window.theme, {
    utils: {
      getVariantsInfo,
      checkAndUpdateQuantity,
      getPcNumbersForLocation,
      calculateRegionInventoryTotal,
      getPickupQuantity,
      getLocalizedRegionName
    },
    addEvent,
    debounceTime,
    delay,
    resizeObserver,
    inViewPort,
    dispatchEvent,
    disableScroll,
    enableScroll,
    drawers: new DrawerManagement(),
    toast: new Toast(),
    header: section,
    debounceEventDispatcher: debounceEvent_default,
    handleQueue,
    localStorage: localStorage2,
    sessionStorage
  });

  // app/scripts/components/lazyScript.js
  var customElementsName = "lazy-script";
  !window.customElements.get(customElementsName) && window.customElements.define(
    customElementsName,
    class extends HTMLElement {
      constructor() {
        super();
        if (!("IntersectionObserver" in window) && !("IntersectionObserverEntry" in window) && !("intersectionRatio" in window.IntersectionObserverEntry.prototype)) {
          this.inVisible();
          return;
        }
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.inVisible();
                observer.disconnect();
              }
            });
          },
          { rootMargin: "400px 0px 400px 0px" }
        );
        observer.observe(this);
      }
      inVisible() {
        setTimeout(() => {
          this.loadFiles();
          this.changeTag();
        }, 100);
      }
      loadFiles() {
        const { file: files } = this.dataset;
        if (!files)
          return;
        this.removeAttribute("data-file");
        files.split(",").forEach((file) => {
          if (file.includes(".js")) {
            const parts = file.split("/");
            const fileName = parts[parts.length - 1].split(".js")[0];
            if (document.querySelector(`script[data-uniq="${fileName}"]`))
              return;
            const script = document.createElement("script");
            script.setAttribute("data-uniq", fileName);
            script.src = file;
            document.head.insertAdjacentElement("beforeend", script);
          } else {
            const parts = file.split("/");
            const fileName = parts[parts.length - 1].split(".css")[0];
            if (document.querySelector(`link[data-uniq="${fileName}"]`))
              return;
            const stylesheet = document.createElement("link");
            stylesheet.href = file;
            stylesheet.rel = "stylesheet";
            stylesheet.type = "text/css";
            stylesheet.media = "all";
            stylesheet.setAttribute("data-uniq", fileName);
            document.head.insertAdjacentElement("beforeend", stylesheet);
          }
        });
      }
      changeTag() {
        const { tag = "div" } = this.dataset;
        if (!tag)
          return;
        const el = document.createElement(tag || "div");
        this.removeAttribute("data-tag");
        Array.from(this.attributes).forEach((attr, i) => {
          el.setAttribute(this.attributes[i].name, this.attributes[i].value);
        });
        Array.from(this.children)?.forEach((child) => {
          el.insertAdjacentElement("beforeend", child);
        });
        this.insertAdjacentElement("beforebegin", el);
        this.remove();
      }
    }
  );

  // app/scripts/utils/toggleClass.js
  var { addEvent: addEvent2 } = theme;
  addEvent2("click", ".jsToggleClass", (e) => {
    const el = e.target.closest(".jsToggleClass");
    const target = document.querySelectorAll(el.dataset.target || null);
    if (!target.length)
      return;
    let { className, classRemove, classRule, classEvent } = el.dataset;
    if (classEvent == "none") {
      e.preventDefault();
      e.stopPropagation();
    }
    target.forEach((i) => {
      switch (classRule) {
        case "add":
          i.classList.add(...className.split(","));
          break;
        case "remove":
          i.classList.remove(...className.split(","));
          break;
        default:
          className.split(",").forEach((c) => i.classList.toggle(c));
      }
      if (classRemove) {
        i.classList.remove(...classRemove.split(","));
      }
      if (i.hasAttribute("data-toggle-inert")) {
        const isActive = i.classList.contains("is-active");
        i.toggleAttribute("inert", !isActive);
        i.setAttribute("aria-hidden", isActive ? "false" : "true");
      }
      i.className.includes("header-search") && document.querySelector("header predictive-search")?.focusSearch();
    });
  });

  // app/scripts/utils/togglePopup.js
  var { addEvent: addEvent3, loadTemplate } = theme;
  var resolvePopup = async (selector) => {
    const popup = document.querySelector(selector);
    if (popup && typeof popup.open === "function")
      return popup;
    await customElements.whenDefined("popup-component");
    return document.querySelector(selector);
  };
  addEvent3("click", ".jsTogglePopup", async (e) => {
    const el = e.target.closest(".jsTogglePopup");
    const { control: popupControlId } = el.dataset;
    let popup = document.querySelector(popupControlId);
    if (!popup)
      return;
    if (popupControlId == "#cartDrawer" && theme.settings.cartStyle == "drawer_desktop" && !window.matchMedia("(min-width: 750px)").matches || popupControlId == "#filterPopup") {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    el.classList.toggle("active");
    popup = await resolvePopup(popupControlId);
    if (!popup || typeof popup.open !== "function")
      return;
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.lastActiveTrigger = el;
      popup.open();
    }
  });
  addEvent3("click", ".jsPopupClose", (e) => {
    e.preventDefault();
    const el = e.target;
    const popup = el.closest(".popup-component");
    if (!popup)
      return;
    popup.close();
    document.querySelectorAll(`.jsTogglePopup[data-control="${popup.dataset.control}"]`)?.forEach((el2) => el2.classList.remove("active"));
  });

  // app/scripts/utils/loadTemplate.js
  var { addEvent: addEvent4 } = theme;
  addEvent4("click", ".jsTemplate", (e) => {
    const node = e.target.closest(".jsTemplate");
    const template = document.querySelector(node.dataset.template);
    if (!template)
      return;
    e.preventDefault();
    e.stopPropagation();
    template.tagName == "TEMPLATE" && Array.from(template.content.children)?.forEach((child) => {
      template.insertAdjacentElement("beforebegin", child);
    });
    if (template.tagName == "NOSCRIPT") {
      const div = document.createElement("div");
      div.innerHTML = template.innerHTML;
      template.insertAdjacentElement("beforebegin", div);
    }
    template.remove();
    node.classList.remove("jsTemplate");
    node.removeAttribute("data-template");
    setTimeout(() => {
      node.click();
    }, 300);
  });

  // app/scripts/utils/tooltipToggle.js
  var { addEvent: addEvent5 } = theme;
  addEvent5("pointerdown", "[data-tooltip-toggle]", function(e) {
    if (!this.contains(document.activeElement))
      return;
    document.activeElement.blur();
    e.preventDefault();
  });

  // app/scripts/utils/keyEvents.js
  document.addEventListener("keyup", (e) => {
    e.key == `Escape` && document.querySelectorAll("details[open]")?.forEach((item) => item.removeAttribute("open"));
  });

  // app/scripts/components/cartAction.js
  if (!customElements.get("cart-action")) {
    const {
      debounceEventDispatcher: debounceEventDispatcher2
    } = theme;
    customElements.define(
      "cart-action",
      class extends HTMLElement {
        constructor() {
          super();
          this.added = false;
          this.quantity = this.querySelector("quantity-input");
          this.init();
        }
        init() {
          this.form = this.querySelector("form");
          this.form.addEventListener("submit", this.handleSubmit.bind(this));
          this.addEventListener("click", (event) => {
            if (event.target.matches("button[type='submit']")) {
              this.handleSubmit(event);
            }
          });
        }
        async handleSubmit(event) {
          event.preventDefault();
          event.stopPropagation();
          if (this.added) {
            return;
          }
          if (!this.form.checkValidity()) {
            const inputs = this.form.elements;
            let invalidInput = null;
            for (const input of inputs) {
              if (!input.validity.valid) {
                invalidInput = input;
                break;
              }
            }
            if (invalidInput) {
              invalidInput.scrollIntoView({ behavior: "smooth", block: "center" });
              invalidInput.focus();
            }
            setTimeout(() => {
              this.form.reportValidity();
            }, 500);
            return;
          }
          this.classList.add("is-atc-loading");
          const formData = new FormData(this.form);
          const quantity = +this.quantity.value;
          formData.delete("properties[quantity]");
          formData.append("quantity", quantity);
          const dataToChange = await Cart.checkItemExists({
            id: this.form.id.value,
            quantity
          });
          if (dataToChange) {
            await Cart.change({
              data: {
                ...dataToChange,
                sections: "drawer-cart-items,header-cart-popup",
                sections_url: `${theme.routes.searchUrl}?q=[variants.id:${this.form.id.value}]`
              },
              action: "add"
            });
          } else {
            formData.append("sections", "drawer-cart-items,header-cart-popup");
            formData.append("sections_url", `${theme.routes.searchUrl}?q=[variants.id:${this.form.id.value}]`);
            await Cart.add(formData);
          }
          this.handleAddedCart();
        }
        handleAddedCart() {
          this.classList.remove("is-atc-loading");
        }
        updateQuantityStep(step) {
          if (!this.quantity)
            return;
          this.quantity.updateStep(step);
          if (step > 1) {
            const messageElm = this.querySelector(".js-sold-multiples-message");
            if (messageElm) {
              messageElm.textContent = messageElm.dataset.message.replace("{{ sold_multiples }}", step);
            }
          } else {
          }
        }
      }
    );
  }

  // app/scripts/components/cartRemove.js
  var customElementsName2 = "cart-remove-button";
  !window.customElements.get(customElementsName2) && window.customElements.define(
    customElementsName2,
    class extends HTMLElement {
      constructor() {
        super();
        this.addEventListener("click", (e) => {
          const { target } = e;
          if (!target.closest("a"))
            return;
          const link = target.closest("a");
          e.preventDefault();
          this.closest("cart-component").dispatchEvent(
            new CustomEvent("change", {
              bubbles: true,
              detail: {
                customElement: link
              }
            })
          );
        });
      }
    }
  );

  // app/scripts/utils/toVariantGid.js
  function toVariantGid(rawId) {
    const id = String(rawId);
    return id.startsWith("gid://") ? id : `gid://shopify/ProductVariant/${id}`;
  }

  // app/scripts/utils/toAmount.js
  function toAmount(price) {
    if (typeof price !== "number" && typeof price !== "string")
      return null;
    const raw = typeof price === "string" ? price.trim() : price;
    if (raw === "")
      return null;
    const amount = Number(raw);
    return Number.isFinite(amount) ? amount : null;
  }

  // app/scripts/utils/chunk.js
  function chunk(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  // app/scripts/utils/livePricing.js
  var REQUEST_TIMEOUT_MS = 10 * 1e3;
  var PRICES_REQUEST_TIMEOUT_MS = 35 * 1e3;
  var BATCH_SIZE = 25;
  var UNPRICED_RETRY_DELAY_MS = 1500;
  function getLivePricingConfig() {
    return window.__livePricingConfig || null;
  }
  function getLpCtx() {
    const value = window.cart_attributes?._lp_ctx;
    return typeof value === "string" ? value : "";
  }
  var LP_CTX_CHANGE_EVENT = "lpCtx:change";
  var LP_CTX_WAIT_MS = 5 * 1e3;
  var resolutionInFlight = null;
  var resolvedLpCtx = null;
  function syncLpCtx(attributes) {
    if (!attributes || typeof attributes !== "object" || !window.cart_attributes)
      return;
    resolvedLpCtx = null;
    const next = typeof attributes._lp_ctx === "string" ? attributes._lp_ctx : "";
    if (next === getLpCtx())
      return;
    window.cart_attributes._lp_ctx = next;
    window.dispatchEvent(new CustomEvent(LP_CTX_CHANGE_EVENT, { detail: { ctx: next } }));
  }
  var latestCartRead = 0;
  async function readCartAttributes() {
    const readId = ++latestCartRead;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
        headers: { Accept: "application/json" },
        signal: controller.signal
      });
      if (!response.ok)
        throw new Error(`cart.js responded ${response.status}`);
      const cart = await response.json();
      if (readId === latestCartRead)
        syncLpCtx(cart?.attributes);
      return cart?.attributes || {};
    } finally {
      clearTimeout(timer);
    }
  }
  function watchLpCtx() {
    const api = window.livePricing;
    if (!api || api.invalidateDisplay?.themeWatchesLpCtx)
      return;
    const appHandler = api.invalidateDisplay;
    const handler = function(...args) {
      if (typeof appHandler === "function")
        appHandler.apply(this, args);
      readCartAttributes().catch(() => {
      });
    };
    handler.themeWatchesLpCtx = true;
    api.invalidateDisplay = handler;
  }
  function waitForLpCtx() {
    if (getLpCtx())
      return Promise.resolve(getLpCtx());
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        window.removeEventListener(LP_CTX_CHANGE_EVENT, onChange);
        resolve(getLpCtx());
      };
      const onChange = () => getLpCtx() && done();
      const timer = setTimeout(done, LP_CTX_WAIT_MS);
      window.addEventListener(LP_CTX_CHANGE_EVENT, onChange);
    });
  }
  async function settleLpCtx() {
    watchLpCtx();
    await Promise.race([
      Promise.resolve(window.theme?.cartAttributesChecked).catch(() => {
      }),
      new Promise((resolve) => setTimeout(resolve, LP_CTX_WAIT_MS))
    ]);
    const attributes = await readCartAttributes();
    if (getLpCtx())
      return getLpCtx();
    if (attributes._fee_province && window.__rslLivePricingContext)
      return waitForLpCtx();
    return "";
  }
  async function resolveLpCtx() {
    const snapshot = getLpCtx();
    if (snapshot)
      return snapshot;
    if (!window.theme?.customer?.isB2B)
      return "";
    if (resolvedLpCtx !== null)
      return resolvedLpCtx;
    resolutionInFlight ||= settleLpCtx().then(
      (ctx) => resolvedLpCtx = ctx,
      () => getLpCtx()
    ).finally(() => {
      resolutionInFlight = null;
    });
    return resolutionInFlight;
  }
  function currencyMatches(config) {
    const active = window.Shopify?.currency?.active;
    if (!active || !config.shopCurrency)
      return true;
    return String(active).toUpperCase() === String(config.shopCurrency).toUpperCase();
  }
  function livePricingMode(config) {
    if (!config)
      return "outage";
    if (!config.displayPrices || !currencyMatches(config))
      return "server-price";
    return "live";
  }
  async function fetchLivePrices(gids, config, ctx) {
    const body = { variants: gids };
    if (config.companyId)
      body.companyId = config.companyId;
    if (config.companyLocationId)
      body.companyLocationId = config.companyLocationId;
    if (ctx)
      body.ctx = ctx;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PRICES_REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`/apps/${config.proxySubpath || "live-pricing"}/api/live-pricing/prices`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
        signal: controller.signal
      });
      if (!response.ok) {
        const error = new Error(`live pricing responded ${response.status}`);
        error.status = response.status;
        throw error;
      }
      const json = await response.json();
      const prices = /* @__PURE__ */ new Map();
      (json?.prices ?? []).forEach(({ variantId, amount, fees }) => {
        if (variantId != null)
          prices.set(toVariantGid(String(variantId)), { amount, fees });
      });
      const retryableGids = Array.isArray(json?.retryableVariantIds) ? new Set(json.retryableVariantIds.map((variantId) => toVariantGid(String(variantId)))) : null;
      return { prices, retryableGids };
    } finally {
      clearTimeout(timer);
    }
  }
  async function fetchLivePriceEntriesBatched(gids, config, ctx, { retryOnly } = {}) {
    const entries = await fetchLivePriceEntriesOnce(gids, config, ctx);
    const retried = await retryUnpricedEntries(entries, config, ctx, { retryOnly });
    retried.forEach((entry, gid) => entries.set(gid, entry));
    return entries;
  }
  async function fetchLivePriceEntriesOnce(gids, config, ctx) {
    const batches = chunk(gids, BATCH_SIZE);
    const settled = await Promise.allSettled(batches.map((batch) => fetchLivePrices(batch, config, ctx)));
    const entries = /* @__PURE__ */ new Map();
    settled.forEach((outcome, index) => {
      const batchResponse = outcome.status === "fulfilled" ? outcome.value : null;
      const failure = outcome.status === "rejected" ? outcome.reason : null;
      const timedOut = failure?.name === "AbortError";
      const refused = failure?.status >= 400 && failure.status < 500;
      batches[index].forEach((gid) => {
        const entry = batchResponse?.prices.get(gid);
        entries.set(gid, {
          amount: toAmount(entry?.amount),
          fees: Array.isArray(entry?.fees) ? entry.fees : void 0,
          requestFailed: failure != null,
          timedOut,
          refused,
          failedUpstream: batchResponse?.retryableGids?.has(gid) ?? true
        });
      });
    });
    return entries;
  }
  function isWorthRetrying({ amount, refused, timedOut, failedUpstream }) {
    return amount == null && !refused && !timedOut && failedUpstream;
  }
  async function retryUnpricedEntries(entries, config, ctx, { retryOnly } = {}) {
    const unpricedGids = [];
    entries.forEach((entry, gid) => {
      if (!isWorthRetrying(entry))
        return;
      if (retryOnly && !retryOnly.has(gid))
        return;
      unpricedGids.push(gid);
    });
    if (!unpricedGids.length)
      return /* @__PURE__ */ new Map();
    await delay(UNPRICED_RETRY_DELAY_MS);
    return fetchLivePriceEntriesOnce(unpricedGids, config, ctx);
  }

  // app/scripts/modules/cart.js
  var { getVariantsInfo: getVariantsInfo2 } = theme.utils;
  var { debounceEventDispatcher } = theme;
  var RSL_ROLE_BUYER_1 = "buyer 1";
  var APP_PROXY_API = "apps/rsl-integrations/api";
  var RETIRED_CART_ATTRIBUTES = [
    "preferred_location",
    "Region",
    "ship_to_id",
    "ship_from_location_id",
    "ship_from_location_name"
  ];
  var LIVE_PRICE_MEMO_TTL_MS = 15 * 1e3;
  var priceMemo = /* @__PURE__ */ new Map();
  var priceMemoExpiresAt = 0;
  function readPriceMemo(gid) {
    if (Date.now() > priceMemoExpiresAt) {
      priceMemo.clear();
      return void 0;
    }
    return priceMemo.get(gid);
  }
  function writePriceMemo(gid, isPriced) {
    const now = Date.now();
    if (now > priceMemoExpiresAt) {
      priceMemo.clear();
      priceMemoExpiresAt = now + LIVE_PRICE_MEMO_TTL_MS;
    }
    priceMemo.set(gid, isPriced);
  }
  function collectVariantIds(data) {
    if (data instanceof FormData)
      return data.getAll("id").filter(Boolean);
    if (Array.isArray(data?.items))
      return data.items.map((item) => item.id).filter(Boolean);
    return data?.id ? [data.id] : [];
  }
  async function unpricedVariantIds(ids, warmIds = []) {
    if (!ids.length)
      return [];
    const config = getLivePricingConfig();
    const mode = livePricingMode(config);
    if (mode === "outage")
      return [...ids];
    if (mode === "server-price")
      return [];
    const gids = ids.map(toVariantGid);
    const answers = /* @__PURE__ */ new Map();
    const unknown = [];
    [.../* @__PURE__ */ new Set([...gids, ...warmIds.map(toVariantGid)])].forEach((gid) => {
      const memoised = readPriceMemo(gid);
      if (memoised === void 0)
        unknown.push(gid);
      else
        answers.set(gid, memoised);
    });
    const requested = new Set(gids);
    if (unknown.some((gid) => requested.has(gid))) {
      const entries = await fetchLivePriceEntriesBatched(unknown, config, await resolveLpCtx(), { retryOnly: requested });
      entries.forEach(({ amount, requestFailed }, gid) => {
        const isPriced = amount != null;
        answers.set(gid, isPriced);
        if (!isPriced && !requestFailed && !requested.has(gid))
          return;
        writePriceMemo(gid, isPriced);
      });
    }
    return ids.filter((id) => answers.get(toVariantGid(id)) !== true);
  }
  async function refusedVariantSkus(refusedIds) {
    try {
      const info = await getVariantsInfo2(refusedIds);
      return refusedIds.map((id) => info?.[id]?.sku).filter(Boolean);
    } catch (error) {
      return [];
    }
  }
  async function livePriceRefusal(refusedIds) {
    const skus = await refusedVariantSkus(refusedIds);
    const named = skus.length ? theme.string?.priceUnavailableCartItems?.replace(/\{\{\s*items\s*\}\}/g, skus.join(", ")) : null;
    const message = named || theme.string?.priceUnavailableCart;
    return { status: 422, message, description: message, livePriceRefusedMessage: message };
  }
  function withoutVariants(data, refusedIds) {
    if (!refusedIds.length)
      return data;
    if (!Array.isArray(data?.items))
      return null;
    const refused = new Set(refusedIds.map(String));
    const items = data.items.filter((item) => !refused.has(String(item.id)));
    return items.length ? { ...data, items } : null;
  }
  var Cart2 = class {
    constructor() {
      this._value = null;
      this._pendingGet = null;
      window.addEventListener("focus", this.get.bind(this));
    }
    setHeaders(data) {
      return data instanceof FormData ? {} : { "Content-Type": "application/json" };
    }
    async value() {
      if (!this._value) {
        await this.get();
      }
      return this._value;
    }
    async get() {
      try {
        if (this._pendingGet) {
          return this._pendingGet;
        }
        this._pendingGet = new Promise(async (resolve, reject) => {
          try {
            this._value = await fetch(`${theme.routes.cartUrl}.js`).then((res) => res.json());
            resolve(this._value);
          } catch (error) {
            reject(error);
          } finally {
            this._pendingGet = null;
          }
        });
        return this._pendingGet;
      } catch (error) {
        throw error;
      }
    }
    // A change that raises a quantity is an add however the caller labels it, and callers reach for
    // the plain label whenever the line is already in the cart. Only a raise needs a price: a
    // decrease or a property edit must stay possible for a line that can no longer be quoted.
    async refuseUnpricedChange(data, action, notifyError) {
      const variantId = data.variantId ?? String(data.id ?? "").split(":")[0];
      if (!variantId)
        return null;
      if (action !== "add" && !await this.raisesQuantity(data))
        return null;
      const refusedIds = await unpricedVariantIds([variantId], this.cartVariantIds());
      if (!refusedIds.length)
        return null;
      const refusal = await livePriceRefusal(refusedIds);
      if (notifyError)
        this.notifyIfError(refusal);
      return refusal;
    }
    // Every line the per-line loops will ask about next, taken from the snapshot the guard already
    // holds. A stale snapshot only costs the lines it is missing their own request.
    cartVariantIds() {
      return (this._value?.items ?? []).map((item) => item.variant_id).filter(Boolean);
    }
    // Only the endpoints that answer with the whole cart may write the snapshot: add.js replies with
    // just the lines it added, and storing that would blank every other line.
    setSnapshot(response) {
      if (response?.item_count == null || !Array.isArray(response.items))
        return;
      this._value = response;
    }
    async raisesQuantity({ id, variantId, quantity }) {
      if (quantity == null)
        return false;
      const lineItem = await this.findLineItem(variantId ?? id);
      return Number(quantity) > Number(lineItem?.properties?._quantity || lineItem?.quantity || 0);
    }
    async add(data, { notifyError = true } = {}) {
      try {
        const refusedIds = await unpricedVariantIds(collectVariantIds(data));
        const refusal = refusedIds.length ? await livePriceRefusal(refusedIds) : null;
        const payload = withoutVariants(data, refusedIds);
        if (!payload) {
          if (notifyError)
            this.notifyIfError(refusal);
          return refusal;
        }
        const response = await fetch(`${theme.routes.cartUrl}/add.js`, {
          method: "POST",
          body: payload instanceof FormData ? payload : JSON.stringify(payload),
          headers: this.setHeaders(payload)
        }).then((res) => res.json());
        if (refusal)
          response.livePriceRefusedMessage = refusal.message;
        if (notifyError)
          this.notifyIfError(response);
        this.get();
        this.updateCartLayout(response, "add");
        return response;
      } catch (error) {
        throw error;
      }
    }
    // notifyError defaults on only for add-flavored changes; quantity/attribute updates stay silent with inline UI.
    async change({ data, updateLayout = true, action = "change", notifyError = action === "add" }) {
      try {
        const refusal = await this.refuseUnpricedChange(data, action, notifyError);
        if (refusal)
          return refusal;
        const dataToChange = { ...data };
        if (data.variantId) {
          const foundLineItem = await this.findLineItem(data.variantId);
          if (!foundLineItem)
            throw new Error("Line item is not found");
          dataToChange.id = foundLineItem.key;
          delete dataToChange.variantId;
        }
        const response = await fetch(`${theme.routes.cartUrl}/change.js`, {
          method: "POST",
          body: JSON.stringify(dataToChange),
          headers: this.setHeaders(dataToChange)
        }).then((res) => res.json());
        this.setSnapshot(response);
        if (notifyError)
          this.notifyIfError(response);
        if (updateLayout) {
          this.get();
          const changedId = String(dataToChange.id ?? "");
          const changedVariantId = changedId.split(":")[0];
          const changed = action === "add" ? response?.items?.find((item) => item.key === changedId || String(item.variant_id) === changedVariantId) : null;
          this.updateCartLayout(changed ? { ...response, product_title: changed.product_title } : response, action);
        }
        return response;
      } catch (error) {
        throw error;
      }
    }
    async update(data) {
      try {
        const response = await fetch(`${theme.routes.cartUrl}/update.js`, {
          method: "POST",
          body: data instanceof FormData ? data : JSON.stringify(data),
          headers: this.setHeaders(data)
        }).then((res) => res.json());
        this.setSnapshot(response);
        this.get();
        this.updateCartLayout(response);
        return response;
      } catch (error) {
        throw error;
      }
    }
    /**
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async updateAttributes(data, updateCartComponent = true) {
      const cartComponent = document.querySelector("cart-component");
      try {
        const dataToSend = { attributes: data };
        cartComponent?.setState?.("loading");
        if (theme.requestType === "cart") {
          dataToSend.sections = cartComponent.id;
        } else if (!theme.customer.isB2B) {
          dataToSend.sections = "drawer-cart-items";
        }
        const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
          method: "POST",
          body: JSON.stringify(dataToSend),
          headers: this.setHeaders(dataToSend)
        }).then((res) => res.json());
        this._value = response;
        syncLpCtx(response?.attributes);
        this.updateCartLayout(response);
        updateCartComponent && cartComponent?.updateLayout?.(response);
        return response;
      } catch (error) {
        throw error;
      } finally {
        cartComponent?.setState?.("reset");
      }
    }
    updateCartLayout(data, action) {
      if (!("sections" in data))
        return;
      theme.header.cart.update(data);
      theme.drawers?.cart?.update(data);
      if (action === "add") {
        theme.toast?.cartAdded?.(data);
      }
    }
    async clear(data) {
      try {
        const response = await fetch(`${theme.routes.cartUrl}/clear.js`, {
          method: "POST",
          body: JSON.stringify(data),
          headers: this.setHeaders(data)
        }).then((res) => res.json());
        this._value = response;
        this.updateCartLayout(response);
        return response;
      } catch (error) {
        throw error;
      }
    }
    // Buyer 1 has no direct checkout: the cart must be submitted for approval instead.
    async requiresApproval() {
      if (!theme.customer.isB2B)
        return false;
      const role = await this.getCustomerRole();
      return !role || role.trim().toLowerCase() === RSL_ROLE_BUYER_1;
    }
    // Deliberately uncached: a role changed in Company Switcher has to take effect on the next page load.
    async getCustomerRole() {
      try {
        const response = await fetch(`${Shopify.routes.root}${APP_PROXY_API}/get-role/app-proxied`, {
          // The proxy signs the customer, but the selected company has to be named explicitly.
          headers: { "x-company-id": theme.customer.company.gid }
        });
        if (!response.ok)
          throw new Error(`get-role responded ${response.status}`);
        const { customRole } = await response.json();
        return customRole;
      } catch (error) {
        console.error("[Cart] getCustomerRole error:", error);
        return null;
      }
    }
    // Creates the pending approval record from the current cart. The customer comes from the signed proxy
    // params and companyId is re-validated against live membership, so neither is trusted from this body.
    async submitForApproval() {
      const cart = await this.get();
      if (!cart.items.length)
        return null;
      const data = {
        companyId: theme.customer.company.gid,
        companyLocationId: theme.customer.company.locationGid,
        currencyCode: cart.currency,
        cartId: cart.token,
        lines: cart.items.map((item) => ({
          variantId: toVariantGid(item.variant_id),
          quantity: item.quantity,
          snapshottedUnitPriceCents: item.final_price
        }))
      };
      const response = await fetch(`${Shopify.routes.root}${APP_PROXY_API}/approvals/app-proxied`, {
        method: "POST",
        headers: this.setHeaders(data),
        body: JSON.stringify(data)
      });
      const result = await response.json().catch(() => null);
      if (!response.ok)
        throw new Error(result?.error || `approval request responded ${response.status}`);
      return result || { success: true };
    }
    addDefaultSections(variantIds) {
      return {
        sections: "drawer-cart-items,header-cart-popup",
        sections_url: `${theme.routes.searchUrl}?q=${variantIds.map((id) => `[variants.id:${id}]`).join(" OR ")}`
      };
    }
    // Cart AJAX endpoints return { status, message, description } on a 4xx (e.g. 422 "sold out");
    // a successful response has no status field. Returns a user-facing message, or null on success.
    getError(result) {
      if (result?.livePriceRefusedMessage)
        return result.livePriceRefusedMessage;
      return result?.status >= 400 ? result.description || result.message : null;
    }
    // Surface a cart AJAX error (e.g. a 422 sold-out) to the shopper via toast. No-op on success.
    notifyIfError(result) {
      const message = this.getError(result);
      if (message)
        theme.toast?.show?.(message, { tone: "error" });
    }
    async checkItemExists({ id, quantity, sumLineItemCount = true }) {
      try {
        const foundLineItem = await this.findLineItem(id);
        if (!foundLineItem) {
          return null;
        }
        const { properties } = foundLineItem;
        const lineItemQuantity = properties?._quantity || foundLineItem.quantity;
        const totalQuantity = sumLineItemCount ? Number(lineItemQuantity) + Number(quantity) : Number(quantity);
        const data = {
          id: foundLineItem.key,
          properties: {
            ...Object.fromEntries(Object.entries(properties).filter(([key]) => !key.startsWith("_b2b_")))
          }
        };
        data.quantity = totalQuantity;
        delete data.properties._quantity;
        delete data.properties._unitPrice;
        return data;
      } catch (error) {
        throw error;
      }
    }
    async checkLineItemsProperties() {
      const cartPage = document.querySelector(".section-main-cart cart-component");
      if (cartPage) {
        await customElements.whenDefined("cart-component");
      }
      try {
        cartPage?.setState("loading");
        const cartValue = await this.value();
        const variantIds = cartValue.items.map((item) => item.variant_id);
        const variantsInfo = await getVariantsInfo2(variantIds);
        const length = cartValue.items.length;
        const sections = location.href.includes("/cart") ? cartPage?.dataset?.sectionIds?.split(",")?.[0] : "drawer-cart-items";
        for (let i = 0; i < length; i++) {
          const lineItem = cartValue.items[i];
          const { properties, key } = lineItem;
          const quantity = Number(properties?._quantity || lineItem.quantity);
          const variantInfo = variantsInfo[lineItem.variant_id];
          const newQuantity = variantInfo?.PCProduct?.MinimumOrderQty > quantity ? variantInfo.PCProduct.MinimumOrderQty : quantity;
          const isLastItem = i === length - 1;
          const dataToChange = await this.checkItemExists({
            id: key,
            quantity: newQuantity,
            sumLineItemCount: false,
            variantInfo
          });
          if (dataToChange.properties._ecofee !== String(variantInfo.ecoFee)) {
            variantInfo.ecoFee > 0 ? dataToChange.properties._ecofee = String(variantInfo.ecoFee) : delete dataToChange.properties._ecofee;
          }
          if (Number(dataToChange.properties?._quantity || dataToChange.quantity) === quantity && this.isPropertiesEqual(dataToChange.properties, properties)) {
            continue;
          }
          const response = await this.change({
            data: {
              ...dataToChange,
              sections
            },
            updateLayout: isLastItem
          });
          cartPage?.updateLayout(response);
        }
      } catch (error) {
        throw error;
      } finally {
        cartPage?.setState("reset");
      }
    }
    async checkB2BAttributes() {
      try {
        const attributesToUpdate = {};
        const isCompanyLocationChanged = String(theme.customer.company.id) !== cart_attributes.company_id || String(window.rsl_customer.WarehouseName) !== cart_attributes.company_location_id;
        const foundLocation = theme.shopLocations.find(
          (location2) => location2.pc_number == window.rsl_customer.WarehouseName
        );
        if (!("preferred_location_id" in window.cart_attributes) || isCompanyLocationChanged) {
          const deliveryMethod = window.cart_attributes.delivery_method || "delivery";
          if (foundLocation) {
            Object.assign(attributesToUpdate, this.generatorPreferredLocationAttributes(deliveryMethod, foundLocation));
          } else {
            const pickupLocation = deliveryMethod === "pickup" && theme.shopLocations.find((location2) => location2.pc_number == window.cart_attributes.preferred_location_id);
            const provinceCode = pickupLocation?.address?.province || window.rsl_customer.ShippingProvince;
            if (provinceCode && window.cart_attributes._fee_province !== provinceCode) {
              attributesToUpdate._fee_province = provinceCode;
            }
          }
        }
        RETIRED_CART_ATTRIBUTES.forEach((attribute) => {
          if (window.cart_attributes[attribute]) {
            attributesToUpdate[attribute] = "";
          }
        });
        if (Object.keys(attributesToUpdate).length === 0)
          return;
        await this.updateAttributes(attributesToUpdate);
        if (!attributesToUpdate.delivery_method && !attributesToUpdate._fee_province)
          return;
        window.dispatchEvent(
          new CustomEvent("deliveryMethod:change", {
            detail: {
              deliveryMethod: attributesToUpdate.delivery_method || window.cart_attributes.delivery_method || "delivery",
              preferredLocation: foundLocation,
              updateHeader: true,
              updateAttributes: false
            }
          })
        );
      } catch (error) {
        throw error;
      }
    }
    async checkNonB2BAttributes() {
      try {
        const attributesToUpdate = {};
        const { attributes } = await this.value();
        [
          "delivery_method",
          "preferred_location",
          "preferred_location_id",
          "region_province_code",
          "_fee_province",
          "company_id",
          "company_location_id",
          "ship_to_id",
          "ship_from_location_id",
          "ship_from_location_name"
        ].forEach((attribute) => {
          if (attribute in attributes) {
            attributesToUpdate[attribute] = "";
          }
        });
        Object.keys(attributesToUpdate).forEach((key) => {
          if (attributesToUpdate[key] === attributes[key]) {
            delete attributesToUpdate[key];
          }
        });
        if (Object.keys(attributesToUpdate).length > 0) {
          await this.updateAttributes(attributesToUpdate);
        }
      } catch (error) {
        throw error;
      }
    }
    async updatePreferredLocation(deliveryMethod = "pickup", preferredLocation) {
      try {
        const attributesToUpdate = this.generatorPreferredLocationAttributes(deliveryMethod, preferredLocation);
        await this.updateAttributes(attributesToUpdate, false);
        this.checkLineItemsProperties();
      } catch (error) {
        throw error;
      }
    }
    // cart/update merges attributes, so preferred_location_id is preserved.
    async updateDeliveryMethod(deliveryMethod = "delivery") {
      try {
        const attributesToUpdate = { delivery_method: deliveryMethod };
        if (deliveryMethod === "delivery") {
          const provinceCode = window.rsl_customer.ShippingProvince;
          if (provinceCode)
            attributesToUpdate._fee_province = provinceCode;
        }
        await this.updateAttributes(attributesToUpdate, false);
        this.checkLineItemsProperties();
      } catch (error) {
        throw error;
      }
    }
    // One entry point for UI-initiated switches so callers don't re-roll the guard + event shape.
    async setDeliveryMethod(target, preferredLocation) {
      if (target === "pickup" && !preferredLocation) {
        throw new Error("setDeliveryMethod('pickup') requires a preferredLocation");
      }
      if (theme.customer?.company?.id) {
        if (target === "pickup") {
          await this.updatePreferredLocation("pickup", preferredLocation);
        } else {
          await this.updateDeliveryMethod("delivery");
        }
      }
      window.dispatchEvent(
        new CustomEvent("deliveryMethod:change", {
          detail: {
            deliveryMethod: target,
            preferredLocation: target === "pickup" ? preferredLocation : void 0,
            updateAttributes: false
          }
        })
      );
    }
    generatorPreferredLocationAttributes(deliveryMethod = "pickup", preferredLocation) {
      const provinceCode = preferredLocation.address.province ?? "";
      return {
        delivery_method: deliveryMethod,
        preferred_location_id: preferredLocation.pc_number,
        company_id: String(theme.customer.company.id),
        company_location_id: String(window.rsl_customer.WarehouseName ?? ""),
        region_province_code: provinceCode,
        _fee_province: provinceCode
      };
    }
    async findLineItem(key) {
      const cartValue = await this.value();
      return cartValue.items.find((item) => item.key == key || item.variant_id == key);
    }
    isPropertiesEqual(a, b) {
      if (a == b)
        return true;
      if (typeof a !== "object" || typeof b !== "object" || a == null || b == null)
        return false;
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length)
        return false;
      for (const key of keysA) {
        if (!keysB.includes(key) || !this.isPropertiesEqual(a[key], b[key]))
          return false;
      }
      return true;
    }
  };
  window.Cart = new Cart2();

  // app/scripts/components/foreignMarkets.js
  var foreignMarkets = class extends HTMLElement {
    constructor() {
      super();
      this.querySelectorAll(".js-multi-shop")?.forEach((item) => {
        item.addEventListener("click", (e) => {
          const element = item;
          const type = element.className.includes("languages") ? "language_code" : "country_code";
          const target = this.querySelector(`[name="${type}"]`);
          target.value = element.dataset.value;
          this.querySelector("form").submit();
        });
      });
      this.querySelectorAll("details").forEach((item) => {
        item.addEventListener("click", (e) => {
          this.querySelectorAll("details").forEach((detail) => {
            if (detail != item) {
              detail.removeAttribute("open");
            }
          });
        });
      });
      this.addEventListener("change", ({ target }) => {
        if (!target.hasAttribute("auto-submit"))
          return;
        this.querySelector("form").submit();
      });
    }
  };
  customElements.define("foreign-markets", foreignMarkets);

  // app/scripts/utils/currencies.js
  var multiCurrency = {};
  var moneyFormat = theme.settings.moneyFormat;
  multiCurrency.moneyFormats = {
    USD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} USD"
    },
    EUR: {
      money_format: "&euro;{{amount}}",
      money_with_currency_format: "&euro;{{amount}} EUR"
    },
    GBP: {
      money_format: "&pound;{{amount}}",
      money_with_currency_format: "&pound;{{amount}} GBP"
    },
    CAD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} CAD"
    },
    ALL: {
      money_format: "Lek {{amount}}",
      money_with_currency_format: "Lek {{amount}} ALL"
    },
    DZD: {
      money_format: "DA {{amount}}",
      money_with_currency_format: "DA {{amount}} DZD"
    },
    AOA: {
      money_format: "Kz{{amount}}",
      money_with_currency_format: "Kz{{amount}} AOA"
    },
    ARS: {
      money_format: "${{amount_with_comma_separator}}",
      money_with_currency_format: "${{amount_with_comma_separator}} ARS"
    },
    AMD: {
      money_format: "{{amount}} AMD",
      money_with_currency_format: "{{amount}} AMD"
    },
    AWG: {
      money_format: "Afl{{amount}}",
      money_with_currency_format: "Afl{{amount}} AWG"
    },
    AUD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} AUD"
    },
    BBD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} Bds"
    },
    AZN: {
      money_format: "m.{{amount}}",
      money_with_currency_format: "m.{{amount}} AZN"
    },
    BDT: {
      money_format: "Tk {{amount}}",
      money_with_currency_format: "Tk {{amount}} BDT"
    },
    BSD: {
      money_format: "BS${{amount}}",
      money_with_currency_format: "BS${{amount}} BSD"
    },
    BHD: {
      money_format: "{{amount}}0 BD",
      money_with_currency_format: "{{amount}}0 BHD"
    },
    BYR: {
      money_format: "Br {{amount}}",
      money_with_currency_format: "Br {{amount}} BYR"
    },
    BZD: {
      money_format: "BZ${{amount}}",
      money_with_currency_format: "BZ${{amount}} BZD"
    },
    BTN: {
      money_format: "Nu {{amount}}",
      money_with_currency_format: "Nu {{amount}} BTN"
    },
    BAM: {
      money_format: "KM {{amount_with_comma_separator}}",
      money_with_currency_format: "KM {{amount_with_comma_separator}} BAM"
    },
    BRL: {
      money_format: "R$ {{amount_with_comma_separator}}",
      money_with_currency_format: "R$ {{amount_with_comma_separator}} BRL"
    },
    BOB: {
      money_format: "Bs{{amount_with_comma_separator}}",
      money_with_currency_format: "Bs{{amount_with_comma_separator}} BOB"
    },
    BWP: {
      money_format: "P{{amount}}",
      money_with_currency_format: "P{{amount}} BWP"
    },
    BND: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} BND"
    },
    BGN: {
      money_format: "{{amount}} \u043B\u0432",
      money_with_currency_format: "{{amount}} \u043B\u0432 BGN"
    },
    MMK: {
      money_format: "K{{amount}}",
      money_with_currency_format: "K{{amount}} MMK"
    },
    KHR: {
      money_format: "KHR{{amount}}",
      money_with_currency_format: "KHR{{amount}}"
    },
    KYD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} KYD"
    },
    XAF: {
      money_format: "FCFA{{amount}}",
      money_with_currency_format: "FCFA{{amount}} XAF"
    },
    CLP: {
      money_format: "${{amount_no_decimals}}",
      money_with_currency_format: "${{amount_no_decimals}} CLP"
    },
    CNY: {
      money_format: "&#165;{{amount}}",
      money_with_currency_format: "&#165;{{amount}} CNY"
    },
    COP: {
      money_format: "${{amount_with_comma_separator}}",
      money_with_currency_format: "${{amount_with_comma_separator}} COP"
    },
    CRC: {
      money_format: "&#8353; {{amount_with_comma_separator}}",
      money_with_currency_format: "&#8353; {{amount_with_comma_separator}} CRC"
    },
    HRK: {
      money_format: "{{amount_with_comma_separator}} kn",
      money_with_currency_format: "{{amount_with_comma_separator}} kn HRK"
    },
    CZK: {
      money_format: "{{amount_with_comma_separator}} K&#269;",
      money_with_currency_format: "{{amount_with_comma_separator}} K&#269;"
    },
    DKK: {
      money_format: "{{amount_with_comma_separator}}",
      money_with_currency_format: "kr.{{amount_with_comma_separator}}"
    },
    DOP: {
      money_format: "RD$ {{amount}}",
      money_with_currency_format: "RD$ {{amount}}"
    },
    XCD: {
      money_format: "${{amount}}",
      money_with_currency_format: "EC${{amount}}"
    },
    EGP: {
      money_format: "LE {{amount}}",
      money_with_currency_format: "LE {{amount}} EGP"
    },
    ETB: {
      money_format: "Br{{amount}}",
      money_with_currency_format: "Br{{amount}} ETB"
    },
    XPF: {
      money_format: "{{amount_no_decimals_with_comma_separator}} XPF",
      money_with_currency_format: "{{amount_no_decimals_with_comma_separator}} XPF"
    },
    FJD: {
      money_format: "${{amount}}",
      money_with_currency_format: "FJ${{amount}}"
    },
    GMD: {
      money_format: "D {{amount}}",
      money_with_currency_format: "D {{amount}} GMD"
    },
    GHS: {
      money_format: "GH&#8373;{{amount}}",
      money_with_currency_format: "GH&#8373;{{amount}}"
    },
    GTQ: {
      money_format: "Q{{amount}}",
      money_with_currency_format: "{{amount}} GTQ"
    },
    GYD: {
      money_format: "G${{amount}}",
      money_with_currency_format: "${{amount}} GYD"
    },
    GEL: {
      money_format: "{{amount}} GEL",
      money_with_currency_format: "{{amount}} GEL"
    },
    HNL: {
      money_format: "L {{amount}}",
      money_with_currency_format: "L {{amount}} HNL"
    },
    HKD: {
      money_format: "${{amount}}",
      money_with_currency_format: "HK${{amount}}"
    },
    HUF: {
      money_format: "{{amount_no_decimals_with_comma_separator}}",
      money_with_currency_format: "{{amount_no_decimals_with_comma_separator}} Ft"
    },
    ISK: {
      money_format: "{{amount_no_decimals}} kr",
      money_with_currency_format: "{{amount_no_decimals}} kr ISK"
    },
    INR: {
      money_format: "Rs. {{amount}}",
      money_with_currency_format: "Rs. {{amount}}"
    },
    IDR: {
      money_format: "{{amount_with_comma_separator}}",
      money_with_currency_format: "Rp {{amount_with_comma_separator}}"
    },
    ILS: {
      money_format: "{{amount}} NIS",
      money_with_currency_format: "{{amount}} NIS"
    },
    JMD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} JMD"
    },
    JPY: {
      money_format: "&#165;{{amount_no_decimals}}",
      money_with_currency_format: "&#165;{{amount_no_decimals}} JPY"
    },
    JEP: {
      money_format: "&pound;{{amount}}",
      money_with_currency_format: "&pound;{{amount}} JEP"
    },
    JOD: {
      money_format: "{{amount}}0 JD",
      money_with_currency_format: "{{amount}}0 JOD"
    },
    KZT: {
      money_format: "{{amount}} KZT",
      money_with_currency_format: "{{amount}} KZT"
    },
    KES: {
      money_format: "KSh{{amount}}",
      money_with_currency_format: "KSh{{amount}}"
    },
    KWD: {
      money_format: "{{amount}}0 KD",
      money_with_currency_format: "{{amount}}0 KWD"
    },
    KGS: {
      money_format: "\u043B\u0432{{amount}}",
      money_with_currency_format: "\u043B\u0432{{amount}}"
    },
    LVL: {
      money_format: "Ls {{amount}}",
      money_with_currency_format: "Ls {{amount}} LVL"
    },
    LBP: {
      money_format: "L&pound;{{amount}}",
      money_with_currency_format: "L&pound;{{amount}} LBP"
    },
    LTL: {
      money_format: "{{amount}} Lt",
      money_with_currency_format: "{{amount}} Lt"
    },
    MGA: {
      money_format: "Ar {{amount}}",
      money_with_currency_format: "Ar {{amount}} MGA"
    },
    MKD: {
      money_format: "\u0434\u0435\u043D {{amount}}",
      money_with_currency_format: "\u0434\u0435\u043D {{amount}} MKD"
    },
    MOP: {
      money_format: "MOP${{amount}}",
      money_with_currency_format: "MOP${{amount}}"
    },
    MVR: {
      money_format: "Rf{{amount}}",
      money_with_currency_format: "Rf{{amount}} MRf"
    },
    MXN: {
      money_format: "$ {{amount}}",
      money_with_currency_format: "$ {{amount}} MXN"
    },
    MYR: {
      money_format: "RM{{amount}} MYR",
      money_with_currency_format: "RM{{amount}} MYR"
    },
    MUR: {
      money_format: "Rs {{amount}}",
      money_with_currency_format: "Rs {{amount}} MUR"
    },
    MDL: {
      money_format: "{{amount}} MDL",
      money_with_currency_format: "{{amount}} MDL"
    },
    MAD: {
      money_format: "{{amount}} dh",
      money_with_currency_format: "Dh {{amount}} MAD"
    },
    MNT: {
      money_format: "{{amount_no_decimals}} &#8366",
      money_with_currency_format: "{{amount_no_decimals}} MNT"
    },
    MZN: {
      money_format: "{{amount}} Mt",
      money_with_currency_format: "Mt {{amount}} MZN"
    },
    NAD: {
      money_format: "N${{amount}}",
      money_with_currency_format: "N${{amount}} NAD"
    },
    NPR: {
      money_format: "Rs{{amount}}",
      money_with_currency_format: "Rs{{amount}} NPR"
    },
    ANG: {
      money_format: "&fnof;{{amount}}",
      money_with_currency_format: "{{amount}} NA&fnof;"
    },
    NZD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} NZD"
    },
    NIO: {
      money_format: "C${{amount}}",
      money_with_currency_format: "C${{amount}} NIO"
    },
    NGN: {
      money_format: "&#8358;{{amount}}",
      money_with_currency_format: "&#8358;{{amount}} NGN"
    },
    NOK: {
      money_format: "kr {{amount_with_comma_separator}}",
      money_with_currency_format: "kr {{amount_with_comma_separator}} NOK"
    },
    OMR: {
      money_format: "{{amount_with_comma_separator}} OMR",
      money_with_currency_format: "{{amount_with_comma_separator}} OMR"
    },
    PKR: {
      money_format: "Rs.{{amount}}",
      money_with_currency_format: "Rs.{{amount}} PKR"
    },
    PGK: {
      money_format: "K {{amount}}",
      money_with_currency_format: "K {{amount}} PGK"
    },
    PYG: {
      money_format: "Gs. {{amount_no_decimals_with_comma_separator}}",
      money_with_currency_format: "Gs. {{amount_no_decimals_with_comma_separator}} PYG"
    },
    PEN: {
      money_format: "S/. {{amount}}",
      money_with_currency_format: "S/. {{amount}} PEN"
    },
    PHP: {
      money_format: "&#8369;{{amount}}",
      money_with_currency_format: "&#8369;{{amount}} PHP"
    },
    PLN: {
      money_format: "{{amount_with_comma_separator}} zl",
      money_with_currency_format: "{{amount_with_comma_separator}} zl PLN"
    },
    QAR: {
      money_format: "QAR {{amount_with_comma_separator}}",
      money_with_currency_format: "QAR {{amount_with_comma_separator}}"
    },
    RON: {
      money_format: "{{amount_with_comma_separator}} lei",
      money_with_currency_format: "{{amount_with_comma_separator}} lei RON"
    },
    RUB: {
      money_format: "&#1088;&#1091;&#1073;{{amount_with_comma_separator}}",
      money_with_currency_format: "&#1088;&#1091;&#1073;{{amount_with_comma_separator}} RUB"
    },
    RWF: {
      money_format: "{{amount_no_decimals}} RF",
      money_with_currency_format: "{{amount_no_decimals}} RWF"
    },
    WST: {
      money_format: "WS$ {{amount}}",
      money_with_currency_format: "WS$ {{amount}} WST"
    },
    SAR: {
      money_format: "{{amount}} SR",
      money_with_currency_format: "{{amount}} SAR"
    },
    STD: {
      money_format: "Db {{amount}}",
      money_with_currency_format: "Db {{amount}} STD"
    },
    RSD: {
      money_format: "{{amount}} RSD",
      money_with_currency_format: "{{amount}} RSD"
    },
    SCR: {
      money_format: "Rs {{amount}}",
      money_with_currency_format: "Rs {{amount}} SCR"
    },
    SGD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} SGD"
    },
    SYP: {
      money_format: "S&pound;{{amount}}",
      money_with_currency_format: "S&pound;{{amount}} SYP"
    },
    ZAR: {
      money_format: "R {{amount}}",
      money_with_currency_format: "R {{amount}} ZAR"
    },
    KRW: {
      money_format: "&#8361;{{amount_no_decimals}}",
      money_with_currency_format: "&#8361;{{amount_no_decimals}} KRW"
    },
    LKR: {
      money_format: "Rs {{amount}}",
      money_with_currency_format: "Rs {{amount}} LKR"
    },
    SEK: {
      money_format: "{{amount_no_decimals}} kr",
      money_with_currency_format: "{{amount_no_decimals}} kr SEK"
    },
    CHF: {
      money_format: "SFr. {{amount}}",
      money_with_currency_format: "SFr. {{amount}} CHF"
    },
    TWD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} TWD"
    },
    THB: {
      money_format: "{{amount}} &#xe3f;",
      money_with_currency_format: "{{amount}} &#xe3f; THB"
    },
    TZS: {
      money_format: "{{amount}} TZS",
      money_with_currency_format: "{{amount}} TZS"
    },
    TTD: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}} TTD"
    },
    TND: {
      money_format: "{{amount}}",
      money_with_currency_format: "{{amount}} DT"
    },
    TRY: {
      money_format: "{{amount}}TL",
      money_with_currency_format: "{{amount}}TL"
    },
    UGX: {
      money_format: "Ush {{amount_no_decimals}}",
      money_with_currency_format: "Ush {{amount_no_decimals}} UGX"
    },
    UAH: {
      money_format: "\u20B4{{amount}}",
      money_with_currency_format: "\u20B4{{amount}} UAH"
    },
    AED: {
      money_format: "Dhs. {{amount}}",
      money_with_currency_format: "Dhs. {{amount}} AED"
    },
    UYU: {
      money_format: "${{amount_with_comma_separator}}",
      money_with_currency_format: "${{amount_with_comma_separator}} UYU"
    },
    VUV: {
      money_format: "${{amount}}",
      money_with_currency_format: "${{amount}}VT"
    },
    VEF: {
      money_format: "Bs. {{amount_with_comma_separator}}",
      money_with_currency_format: "Bs. {{amount_with_comma_separator}} VEF"
    },
    VND: {
      money_format: "{{amount_no_decimals_with_comma_separator}}&#8363;",
      money_with_currency_format: "{{amount_no_decimals_with_comma_separator}} VND"
    },
    XBT: {
      money_format: "{{amount_no_decimals}} BTC",
      money_with_currency_format: "{{amount_no_decimals}} BTC"
    },
    XOF: {
      money_format: "CFA{{amount}}",
      money_with_currency_format: "CFA{{amount}} XOF"
    },
    ZMW: {
      money_format: "K{{amount_no_decimals_with_comma_separator}}",
      money_with_currency_format: "ZMW{{amount_no_decimals_with_comma_separator}}"
    }
  };
  multiCurrency.formatMoney = function(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    let value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat;
    function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
      if (isNaN(number) || number == null) {
        return 0;
      }
      number = (number / 100).toFixed(precision);
      const parts = number.split(".");
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    }
    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }
    return formatString.replace(placeholderRegex, value);
  };
  Object.assign(Number.prototype, {
    toCurrency: function() {
      return multiCurrency.formatMoney(this, moneyFormat);
    }
  });
  Object.assign(String.prototype, {
    toCurrency: function() {
      return multiCurrency.formatMoney(this, moneyFormat);
    }
  });

  // app/scripts/components/quantity-input.js
  var { debounceTime: debounceTime2 } = theme;
  var QuantityInput = class extends HTMLElement {
    constructor() {
      super();
      const minusBtn = this.querySelector(".js-minus-btn");
      const plusBtn = this.querySelector(".js-plus-btn");
      const input = this.querySelector(".js-quantity-input");
      this.prevValue = input.value;
      this.input = input;
      this.onChangeCallbacks = [];
      if (!minusBtn || !plusBtn || !input)
        return;
      this.min = this.#getNumericAttr("min", 1);
      this.max = this.#getNumericAttr("max", Infinity);
      plusBtn.addEventListener("click", this.increment.bind(this));
      minusBtn.addEventListener("click", this.decrement.bind(this));
      input.addEventListener("focus", this.handleInputFocus.bind(this));
      input.addEventListener(
        "input",
        debounceTime2(() => {
          const value = Number(input.value);
          if (isNaN(value))
            return;
          if (value > this.max) {
            input.value = this.max;
          }
          if (!this.validateStep()) {
            return;
          }
          this.onChangeCallbacks.forEach((cb) => cb(Number(input.value)));
        }, 500)
      );
      input.addEventListener("blur", this.handleInputBlur.bind(this));
    }
    validateAndDispatch(event) {
      const { min, step, value } = this.input;
      if (!this.validateStep()) {
        const nMin = Number(min) || 0;
        const nStep = Number(step) || 1;
        const nValue = Math.max(Number(value) || 0, nMin);
        const stepsFromMin = Math.ceil((nValue - nMin) / nStep);
        this.input.value = String(nMin + stepsFromMin * nStep);
        this.input.setCustomValidity("");
      }
      if (this.prevValue && this.prevValue == this.input.value)
        return;
      this.#dispatchChange(Number(this.input.value));
      this.prevValue = this.input.value;
    }
    handleInputBlur(event) {
      event.stopPropagation();
      if (!this.input.value.length && this.prevValue) {
        this.input.value = this.prevValue;
      }
      this.validateAndDispatch(event);
      this.prevValue = null;
    }
    handleInputFocus(e) {
      e.stopPropagation();
      this.prevValue = this.input.value;
      this.input.value = "";
    }
    increment(e) {
      e.stopPropagation();
      const current = Number(this.input.value) || 0;
      if (current < this.max) {
        this.input.stepUp();
        this.validateAndDispatch();
      }
    }
    decrement(e) {
      e.stopPropagation();
      const current = Number(this.input.value) || 0;
      const nextValue = current - this.#getNumericAttr("step", 1);
      if (nextValue <= this.min) {
        this.input.value = this.min;
      }
      if (current > this.min) {
        this.input.stepDown();
      }
      this.validateAndDispatch();
    }
    get value() {
      return +this.input.value;
    }
    set value(value) {
      if (!Number(value)) {
        console.warn("QuantityInput: value is not a number");
        return;
      }
      this.input.value = String(value);
    }
    get step() {
      return +this.input.step;
    }
    stepUp(value) {
      if (!value)
        return;
      this.input.stepUp(Number(value));
      this.validateAndDispatch();
    }
    stepDown(value) {
      if (!value)
        return;
      this.input.stepDown(Number(value));
      this.validateAndDispatch();
    }
    onChange(cb) {
      this.onChangeCallbacks.push(cb);
    }
    updateStep(step) {
      if (step == this.input.step)
        return;
      const currentValue = this.input.value;
      this.input.setAttribute("step", step);
      this.input.setAttribute("min", step);
      this.min = this.#getNumericAttr("min", 1);
      if (this.value < step) {
        this.input.value = step;
      } else if ((this.value - this.min) % step !== 0) {
        const stepsFromMin = Math.floor((this.value - this.min) / step);
        this.input.value = String(this.min + stepsFromMin * step);
      }
      if (currentValue != this.input.value) {
        this.validateAndDispatch();
      }
    }
    validateStep() {
      const { min, step, value } = this.input;
      if (!this.input.value.length)
        return false;
      const nMin = Number(min) || 0;
      const nStep = Number(step) || 1;
      const nValue = Number(value);
      if (!Number.isFinite(nValue) || nValue < nMin)
        return false;
      const remainder = (nValue - nMin) % nStep;
      const isOnStep = Math.abs(remainder) < Number.EPSILON || Math.abs(remainder - nStep) < Number.EPSILON;
      if (!isOnStep) {
        this.input.setCustomValidity(`The value must be a multiple of ${nStep} from ${nMin}`);
        this.input.reportValidity();
        return false;
      }
      this.input.setCustomValidity("");
      return true;
    }
    #getNumericAttr(name, fallback) {
      const raw = this.input?.getAttribute(name);
      const n = Number(raw);
      return Number.isFinite(n) ? n : fallback;
    }
    #dispatchChange(numericValue) {
      this.onChangeCallbacks.forEach((cb) => cb(numericValue));
    }
  };
  customElements.define("quantity-input", QuantityInput);

  // app/scripts/components/drawer.js
  var { disableScroll: disableScroll2, enableScroll: enableScroll2 } = theme;
  var Drawer = class extends HTMLElement {
    constructor() {
      super();
      this.initCloseButtons();
    }
    open() {
      this.classList.add("is-open");
      this.updateTriggerState(true);
      disableScroll2();
    }
    close() {
      this.classList.remove("is-open");
      this.updateTriggerState(false);
      enableScroll2();
    }
    updateTriggerState(isExpanded) {
      if (!this.id)
        return;
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((button) => {
        button.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      });
    }
    initCloseButtons() {
      const closeButtons = this.querySelectorAll(".js-close-button");
      closeButtons.forEach((button) => {
        if (button.dataset.boundDrawerClose === "true")
          return;
        button.dataset.boundDrawerClose = "true";
        button.addEventListener("click", () => this.close());
      });
    }
  };

  // app/scripts/components/CartDrawer.js
  var { disableScroll: disableScroll3, enableScroll: enableScroll3 } = theme;
  var CartDrawer = class extends Drawer {
    constructor() {
      super();
    }
    async connectedCallback() {
      await customElements.whenDefined("cart-component");
      this.cartComponent = this.querySelector("cart-component");
      this.cartContainer = this.cartComponent.querySelector("main");
      document.addEventListener("keyup", this.handleKeyUp.bind(this));
    }
    update(data) {
      this.cartComponent?.updateLayout(data);
      return this;
    }
    open(lineItemKey) {
      this.classList.add("is-open");
      disableScroll3();
      if (lineItemKey)
        setTimeout(() => this.scrollTo(lineItemKey), 200);
    }
    close() {
      this.classList.remove("is-open");
      enableScroll3();
      this.scrollToTop();
    }
    scrollTo(key) {
      const foundLineItem = this.cartComponent.findLineItem(key);
      foundLineItem?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
    scrollToTop() {
      this.cartContainer.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
    handleKeyUp(event) {
      switch (event.key) {
        case "Escape":
          this.close();
          break;
      }
    }
  };
  customElements.define("cart-drawer", CartDrawer);

  // app/scripts/components/HeaderCart.js
  var VISIBLE_CART_COUNT_SELECTOR = "[data-cart-visible-count]";
  var HeaderCart = class extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.listenClick();
    }
    update(data) {
      try {
        if (!data)
          return;
        const { sections } = data;
        if (!sections)
          throw new Error("sections is required");
        const section2 = sections[this.sectionId];
        if (!section2)
          return;
        const layout = new DOMParser().parseFromString(section2, "text/html");
        const elementsToUpdate = this.querySelectorAll(".js-layout-update");
        elementsToUpdate.forEach((elm) => {
          const foundElement = layout.getElementById(elm.dataset.updateId);
          if (foundElement) {
            elm.innerHTML = foundElement.innerHTML;
          }
        });
        const cartItemCount = layout.getElementById("header-cart-popup-item-count");
        const count = cartItemCount.innerHTML.trim();
        this.dataset.count = count;
        this.updateVisibleCartLabels(count);
        return this;
      } catch (error) {
        throw error;
      }
    }
    updateVisibleCartLabels(count) {
      const itemCount = Number.parseInt(count, 10) || 0;
      document.querySelectorAll(VISIBLE_CART_COUNT_SELECTOR).forEach((label) => {
        label.textContent = itemCount > 0 ? ` (${itemCount})` : "";
      });
    }
    listenClick() {
      this.addEventListener("click", (e) => {
        if (theme.drawers?.openCart())
          e.preventDefault();
      });
    }
  };
  customElements.define("header-cart", HeaderCart);

  // app/scripts/utils/sanitizeId.js
  function sanitizeId(id = "") {
    const value = String(id);
    if (value.includes("gid://shopify")) {
      return value.split("/").pop();
    }
    return value;
  }

  // app/scripts/modules/my-lists/my-lists.api.js
  var MyListsAPI = {
    async create(data) {
      return await teifiWishlistApi.create(data);
    },
    async delete(myListId) {
      if (!myListId)
        throw new Error("No myListId provided");
      return await teifiWishlistApi.delete(myListId);
    },
    async fetchList(options = {}) {
      const { search = "", sort = "updatedAt:desc", page = 1, limit = 10 } = options;
      const response = await teifiWishlistApi.find({
        search,
        sort,
        page,
        limit
      });
      response.items = response.items.map((item) => ({
        ...item,
        id: item.metaobjectGid.split("/").pop()
      }));
      return response;
    },
    async fetchMyList(id) {
      return await teifiWishlistApi.findById(sanitizeId(id));
    },
    async update(myListId, data) {
      if (!myListId)
        throw new Error("No myListId provided");
      return await teifiWishlistApi.update(sanitizeId(myListId), data);
    },
    async sendShare(myListId, data) {
      const { emails, senderName, ...rest } = data;
      if (!emails || !senderName)
        throw new Error("Missing required fields");
      const response = await Promise.all(
        emails.split(",").map(async (email) => {
          const res = await teifiWishlistApi.updateCustomerPermission(myListId, {
            email: email.trim(),
            senderName,
            ...rest
          });
          if (res.error) {
            return { ...res, email };
          }
          return res;
        })
      );
      if (response.some((item) => item.error)) {
        throw new Error(
          response.filter((item) => item.error).splice(0, 5).map((item) => `${item.email}: ${item.error}`).join("<br>")
        );
      }
      return response;
    },
    async sendCopy(myListId, data) {
      const { emails, senderName, ...rest } = data;
      if (!emails || !senderName)
        throw new Error("Missing required fields");
      const response = await Promise.all(
        emails.split(",").map(async (email) => {
          const res = await teifiWishlistApi.copy(myListId, {
            email: email.trim(),
            senderName,
            ...rest
          });
          if (res.error) {
            return { ...res, email };
          }
          return res;
        })
      );
      if (response.some((item) => item.error)) {
        throw new Error(
          response.filter((item) => item.error).splice(0, 5).map((item) => `${item.email}: ${item.error}`).join("<br>")
        );
      }
      return response;
    },
    async updateCustomerPermission(myListId, data) {
      return await teifiWishlistApi.updateCustomerPermission(myListId, data);
    },
    async updateCompanyPermission(myListId, data) {
      return await teifiWishlistApi.updateCompanyPermission(myListId, data);
    }
  };
  var my_lists_api_default = MyListsAPI;

  // app/scripts/modules/my-lists/index.js
  function assertMyListResponse(response) {
    if (!response || response.error)
      throw new Error(response?.error || "My list request failed");
    return response;
  }
  function normalizeMyList(myList = {}) {
    const id = sanitizeId(myList.id || myList.metaobjectGid);
    return {
      ...myList,
      id,
      items: Array.isArray(myList.items) ? myList.items : [],
      customerPermissions: Array.isArray(myList.customerPermissions) ? myList.customerPermissions : [],
      activityLogs: Array.isArray(myList.activityLogs) ? myList.activityLogs : []
    };
  }
  var MyLists = class {
    constructor() {
      this.list = /* @__PURE__ */ new Map();
      this.isFetchedList = false;
      this.isFetchedAll = false;
      this.modal = {
        create: document.querySelector("popup-my-list"),
        update: document.querySelector("popup-my-list"),
        delete: document.querySelector("popup-delete-confirm"),
        share: document.querySelector("popup-share-list"),
        copyList: document.querySelector("popup-copy-list"),
        selectMyList: document.querySelector("popup-select-my-list")
      };
      this.addedProductsList = /* @__PURE__ */ new Map();
      this.checkAddedProducts();
    }
    async create(data) {
      this.validate(data);
      const createdMyList = this.remember(assertMyListResponse(await my_lists_api_default.create(data)));
      this.invalidateListCache();
      return createdMyList;
    }
    async delete(myListId) {
      const response = await my_lists_api_default.delete(myListId);
      this.list.delete(sanitizeId(myListId));
      this.invalidateListCache();
      return response;
    }
    validate(data) {
      ["name", "description", "items", "customerGid"].forEach((field) => {
        if (!(field in data) || data[field] === void 0) {
          throw new Error(`${field} is required`);
        }
      });
    }
    /**
     * Updates a list with the provided data
     * @async
     * @param {number|string} myListId - The ID of the list to update
     * @param {Object} data - The data to update the list with
     * @param {string} [data.name] - The new name for the list
     * @param {string} [data.description] - The new description for the list
     * @returns {Promise<Object>} A promise that resolves to the updated list
     * @throws {Error} If there's an error updating the list
     */
    async update(myListId, data) {
      this.validate(data);
      const updatedMyList = this.remember(assertMyListResponse(await my_lists_api_default.update(myListId, data)));
      this.invalidateListCache();
      return updatedMyList;
    }
    remember(myList) {
      const normalized = normalizeMyList(myList);
      if (normalized.id) {
        this.list.set(normalized.id, normalized);
      }
      return normalized;
    }
    invalidateListCache() {
      this.isFetchedList = false;
      this.isFetchedAll = false;
    }
    async sendShare(myListId, data) {
      return await my_lists_api_default.sendShare(myListId, data);
    }
    async sendCopy(myListId, data) {
      return await my_lists_api_default.sendCopy(myListId, data);
    }
    async share(myListId) {
      this.modal.share.open(myListId);
    }
    /**
     * Fetches the lists with optional filtering and sorting.
     * @async
     * @param {Object} options - The options for fetching lists
     * @param {string} [options.search] - Search term to filter lists
     * @param {string} [options.sort] - Sort criteria for lists
     * @returns {Promise<Array>} A promise that resolves to an array of lists
     * @throws {Error} If there's an error fetching the lists
     */
    async fetchMyLists(options = {}) {
      const response = await my_lists_api_default.fetchList(options);
      response.items = response.items.map(normalizeMyList);
      response.items.forEach((item) => this.remember(item));
      this.isFetchedList = true;
      return response;
    }
    async getAll(options = {}) {
      const { forceRefresh = false } = typeof options === "boolean" ? { forceRefresh: options } : options;
      if (forceRefresh) {
        this.invalidateListCache();
        this.list.clear();
      }
      if (this.isFetchedAll) {
        return [...this.list.values()];
      }
      let { totalPages, items, page } = await my_lists_api_default.fetchList();
      const totalItemList = [...items.map(normalizeMyList)];
      while (totalPages > page) {
        const response = await my_lists_api_default.fetchList({ page: page + 1 });
        totalItemList.push(...response.items.map(normalizeMyList));
        page++;
      }
      totalItemList.forEach((item) => this.remember(item));
      this.isFetchedAll = true;
      return totalItemList;
    }
    async fetchMyList(id) {
      if (this.list.get(sanitizeId(id))) {
        return this.list.get(sanitizeId(id));
      }
      return this.remember(await my_lists_api_default.fetchMyList(id));
    }
    async getMyLists() {
      if (!this.isFetchedList) {
        await this.fetchMyLists();
      }
      return [...this.list.values()];
    }
    async getById(myListId) {
      return this.list.get(sanitizeId(myListId)) || await this.fetchMyList(myListId);
    }
    copyListFrom(originMyListId) {
      return {
        to: async (targetMyListId) => {
          return await this.copyList(originMyListId, targetMyListId);
        }
      };
    }
    async copyList(originMyListId, targetMyListId) {
      const originMyList = await this.getById(originMyListId);
      const targetMyList = await this.getById(targetMyListId);
      const targetItemsIds = targetMyList.items.map(({ variantGid }) => variantGid);
      const filteredItems = originMyList.items.filter(({ variantGid }) => !targetItemsIds.includes(variantGid));
      return await this.update(targetMyListId, {
        ...targetMyList,
        items: [...targetMyList.items, ...filteredItems]
      });
    }
    toListItem({ handle, variantId, productId, quantity = 1 }) {
      return {
        handle,
        variantGid: `gid://shopify/ProductVariant/${variantId}`,
        productGid: `gid://shopify/Product/${productId}`,
        quantity
      };
    }
    async renderMyListOptions(selectElm, disabledMyListIds = []) {
      const list = await this.getAll();
      [...selectElm.options].forEach((option, index) => {
        if (index > 0)
          option.remove();
      });
      list.forEach((myList) => {
        const option = document.createElement("option");
        option.value = myList.id;
        option.textContent = myList.name;
        if (disabledMyListIds.includes(myList.id)) {
          option.disabled = true;
        }
        selectElm.appendChild(option);
      });
    }
    async checkAddedProducts() {
      await isTeifiWishlistReady();
      const myListButtons = document.querySelectorAll(".js-my-list-button:not(.is-initialized)");
      if (!myListButtons.length)
        return;
      const myListIds = new Set([...myListButtons].map((button) => button.dataset.productId));
      const response = await teifiWishlistApi.getProductWishlistsByProductIds([...myListIds.values()]);
      const myListMap = response.reduce((prev, next) => {
        prev[next.productId] = next.wishlists;
        return prev;
      }, {});
      myListButtons.forEach((button) => {
        const wishlists = myListMap[button.dataset.productId];
        if (wishlists.length) {
          button.classList.add("is-added");
        }
      });
    }
    async isAddedToList(variantGid) {
      const myLists = await this.getAll();
      return myLists.some((myList) => myList.items.some((item) => item.variantGid === variantGid));
    }
    async updateCompanyPermissions(myListId, data) {
      return await my_lists_api_default.updateCompanyPermission(myListId, data);
    }
    async updateCustomerPermission(myListId, data) {
      return await my_lists_api_default.updateCustomerPermission(myListId, data);
    }
    async fetchSharedMyList(id) {
      const url = `${teifiWishlistApi.baseUrl}/api/public/wishlists/${id}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch shared list: ${response.statusText}`);
      }
      return response.json();
    }
  };
  var my_lists_default = MyLists;

  // app/scripts/utils/resolveInventoryState.js
  var STOCK_STATES = {
    IN_STOCK: "in-stock",
    OBS_BRANCH: "obs-branch",
    OBS_REGION: "obs-region",
    OOB: "oob"
  };
  function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
  }
  function normalizePcNumber(value) {
    return value == null ? "" : String(value);
  }
  function resolveInventoryState(payload, options = {}) {
    const locations = Array.isArray(payload?.locations) ? payload.locations : [];
    const anchor = options.selectedLocation || payload?.selected || {};
    const selectedPc = normalizePcNumber(anchor.pc_number);
    const selectedRegion = anchor.region || "";
    const selected = locations.find((location2) => normalizePcNumber(location2.pc_number) === selectedPc);
    const selectedQty = toNumber(selected?.qty);
    const locationName = selected?.name || selected?.pc_number || anchor.name || "";
    const regionTotal = locations.filter((location2) => location2.region === selectedRegion).reduce((sum, location2) => sum + toNumber(location2.qty), 0);
    const totalQuantity = locations.reduce((sum, location2) => sum + toNumber(location2.qty), 0);
    const sameRegionOthers = regionTotal - selectedQty;
    const otherRegionQty = totalQuantity - regionTotal;
    const pickupQuantity = selectedQty;
    const deliveryQuantity = options.b2bDeliveryQuantity === "region" ? regionTotal : pickupQuantity;
    let state;
    let displayQuantity;
    if (selectedQty > 0) {
      state = STOCK_STATES.IN_STOCK;
      displayQuantity = selectedQty;
    } else if (sameRegionOthers > 0) {
      state = STOCK_STATES.OBS_BRANCH;
      displayQuantity = sameRegionOthers;
    } else if (otherRegionQty > 0) {
      state = STOCK_STATES.OBS_REGION;
      displayQuantity = otherRegionQty;
    } else {
      state = STOCK_STATES.OOB;
      displayQuantity = 0;
    }
    return { state, displayQuantity, locationName, pickupQuantity, regionTotal, totalQuantity, deliveryQuantity };
  }
  function readInventoryState(root = document) {
    const el = root?.querySelector?.("script[data-inventory-state]");
    if (!el?.textContent)
      return null;
    try {
      const data = JSON.parse(el.textContent);
      return data && Array.isArray(data.locations) ? data : null;
    } catch (error) {
      console.error("[inventory] Failed to parse data-inventory-state:", error);
      return null;
    }
  }

  // app/scripts/components/CardProductStateRuntime.js
  var LABELS = {
    "in-stock": "In Stock",
    "obs-branch": "Available nearby",
    "obs-region": "Available in other regions",
    oob: "Out of Stock"
  };
  var STOCK_STATUS_CLASSES = Object.values(STOCK_STATES).map(
    (state) => `card-product-state__inventory-status--${state}`
  );
  var STOCK_MODIFIER_CLASSES = Object.values(STOCK_STATES).map((state) => `is-${state}`);
  var CardProductStateRuntime = class {
    constructor(card) {
      this.card = card;
      this.status = card.querySelector("[data-card-state-stock-status]");
      this.label = card.querySelector("[data-card-state-stock-label]");
      this.count = card.querySelector("[data-card-state-stock-count]");
      this.divider = card.querySelector("[data-card-state-stock-divider]");
      this.location = card.querySelector("[data-card-state-stock-location]");
      this.heading = card.querySelector("[data-card-state-stock-heading]");
      this.callBranch = card.querySelector("[data-card-state-call-branch]");
      this.action = card.querySelector(".card-product-state__cta");
      this.quantity = card.querySelector("[data-card-state-quantity]");
    }
    // Native path: canonical { selected, locations } payload from data-inventory-state.
    updateFromState(payload, selectedLocation = window.theme?.preferredLocation) {
      if (!this.status || !payload || !Array.isArray(payload.locations))
        return;
      const result = resolveInventoryState(payload, {
        selectedLocation,
        b2bDeliveryQuantity: window.theme?.settings?.b2bDeliveryQuantity
      });
      this.applyStockState(result);
    }
    applyStockState({ state, displayQuantity, locationName }) {
      this.card.dataset.stockState = state;
      this.card.classList.remove(...STOCK_MODIFIER_CLASSES);
      this.card.classList.add(`is-${state}`);
      this.card.classList.toggle("stock-unavailable", state === STOCK_STATES.OOB);
      this.status.classList.remove(...STOCK_STATUS_CLASSES);
      this.status.classList.add(`card-product-state__inventory-status--${state}`);
      if (this.label)
        this.label.textContent = window.theme?.string?.productCard?.[state] || LABELS[state] || LABELS.oob;
      if (this.count)
        this.count.textContent = displayQuantity;
      if (this.location)
        this.location.textContent = locationName || "";
      const isInStock = state === STOCK_STATES.IN_STOCK;
      const isOob = state === STOCK_STATES.OOB;
      this.count?.classList.toggle("hidden", !isInStock);
      this.divider?.classList.toggle("hidden", !isInStock);
      this.location?.classList.toggle("hidden", !isInStock);
      this.callBranch?.classList.toggle("hidden", !isOob);
      this.action?.classList.toggle("hidden", isOob);
      this.quantity?.classList.toggle("card-product-state__quantity--hidden", isOob);
    }
    updateDeliveryMethod(method) {
      this.card.dataset.deliveryMethod = method;
      if (!this.heading)
        return;
      const key = method === "pickup" ? "pickup" : "shipping";
      const label = window.theme?.string?.productCard?.[key];
      if (label)
        this.heading.textContent = label;
    }
  };

  // app/scripts/components/CardProduct.js
  var CARRIED_PARAM_KEYS = ["pc", "sc"];
  function withCarriedParams(value, params) {
    if (!value)
      return value;
    const url = new URL(value, window.location.origin);
    CARRIED_PARAM_KEYS.forEach((key) => {
      if (params.has(key))
        url.searchParams.set(key, params.get(key));
    });
    return `${url.pathname}${url.search}${url.hash}`;
  }
  var CardProduct = class extends HTMLElement {
    _price = null;
    _hasLivePrice = false;
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.quantity = this.querySelector("quantity-input");
      this.JSONData = JSON.parse(this.querySelector("script[data-json]")?.textContent || "{}");
      this.unitPriceLabel = this.querySelector(".js-unit-price");
      this._price = this.dataset.price ? Number(this.dataset.price) / 100 : null;
      this.currentEcoFee = this.dataset.ecoFee ? Number(this.dataset.ecoFee) / 100 : 0;
      this.erpNumber = this.dataset.erpNumber;
      this.stateRuntime = this.querySelector("[data-card-state-stock-status]") ? new CardProductStateRuntime(this) : null;
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleDeliveryMethodChange = this.handleDeliveryMethodChange.bind(this);
      this.handleOptionClick = this.handleOptionClick.bind(this);
    }
    // The server price excludes fees, but a live amount is already what the cart charges, fees included.
    get price() {
      return this._hasLivePrice ? this._price : this._price + this.currentEcoFee;
    }
    // Zero without a confirmed price: the row hides its own line total, so the page totals that sum
    // these must not keep counting the server price it no longer shows.
    get lineTotal() {
      if (this.classList.contains("price-unavailable"))
        return 0;
      return this.price * Number(this.quantity?.value || 0);
    }
    async connectedCallback() {
      await customElements.whenDefined("quantity-input");
      this.form?.addEventListener("submit", this.handleSubmit);
      this.optionsList = this.querySelector(".card-product-state__options");
      this.optionsList?.addEventListener("click", this.handleOptionClick);
      if (this.stateRuntime) {
        this.initNativeStockState();
        window.addEventListener("deliveryMethod:change", this.handleDeliveryMethodChange);
        const currentMethod = document.querySelector("header-delivery-method input.delivery-method:checked")?.value;
        if (currentMethod)
          this.stateRuntime.updateDeliveryMethod(currentMethod);
      }
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.form?.removeEventListener("submit", this.handleSubmit);
      this.optionsList?.removeEventListener("click", this.handleOptionClick);
      window.removeEventListener("deliveryMethod:change", this.handleDeliveryMethodChange);
    }
    async handleOptionClick(event) {
      const button = event.target.closest("button[data-variant-id]");
      if (!button || button.classList.contains("is-selected"))
        return;
      const variantId = button.dataset.variantId;
      this.classList.add("is-fetching-price");
      const submitButton = this.form?.querySelector('[type="submit"]');
      if (submitButton)
        submitButton.disabled = true;
      try {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const response = await fetch(
          `${Shopify.routes.root}variants/${variantId}?section_id=card-product-state-variant`,
          {
            signal: this.abortController.signal
          }
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch variant ${variantId}: ${response.status}`);
        }
        const html = await response.text();
        const nextCard = new DOMParser().parseFromString(html, "text/html").querySelector("card-product");
        if (!nextCard) {
          throw new Error(`No card markup returned for variant ${variantId}`);
        }
        const currentProductLink = this.querySelector('a[href*="/products/"]');
        const currentParams = currentProductLink ? new URL(currentProductLink.href).searchParams : null;
        if (currentParams && CARRIED_PARAM_KEYS.some((key) => currentParams.has(key))) {
          nextCard.querySelectorAll('a[href*="/products/"]').forEach((link) => {
            link.setAttribute("href", withCarriedParams(link.getAttribute("href"), currentParams));
          });
          nextCard.querySelectorAll("[data-replacement-current-url]").forEach((element) => {
            element.dataset.replacementCurrentUrl = withCarriedParams(
              element.dataset.replacementCurrentUrl,
              currentParams
            );
          });
        }
        const hadFocus = this.contains(document.activeElement);
        this.replaceWith(nextCard);
        if (hadFocus)
          nextCard.querySelector(`button[data-variant-id="${variantId}"]`)?.focus();
        window.themeLivePrice?.refresh(nextCard);
      } catch (error) {
        if (error.name === "AbortError")
          return;
        console.warn("Card product: variant switch failed", error);
        this.classList.remove("is-fetching-price");
        if (submitButton)
          submitButton.disabled = false;
      }
    }
    handleDeliveryMethodChange(event) {
      const method = event.detail?.deliveryMethod;
      if (method)
        this.stateRuntime?.updateDeliveryMethod(method);
      const loc = event.detail?.preferredLocation;
      if (loc && this.nativeInventory) {
        this.nativeSelected = loc;
        this.applyNativeStockState();
      }
    }
    initNativeStockState() {
      this.nativeInventory = readInventoryState(this);
      if (!this.nativeInventory)
        return;
      this.nativeSelected = this.nativeInventory.selected || null;
      this.applyNativeStockState();
    }
    applyNativeStockState() {
      if (!this.stateRuntime || !this.nativeInventory)
        return;
      this.stateRuntime.updateFromState(this.nativeInventory, this.nativeSelected);
    }
    get properties() {
      const properties = {};
      if (this.currentEcoFee) {
        properties._ecofee = this.currentEcoFee;
      }
      return properties;
    }
    async handleSubmit(event) {
      try {
        event.preventDefault();
        this.setState("adding");
        const formData = new FormData(this.form);
        const quantity = +formData.get("quantity");
        const id = this.form.id.value;
        formData.append("sections", "drawer-cart-items,header-cart-popup");
        formData.append("sections_url", `${theme.routes.searchUrl}?q=[variants.id:${id}]`);
        if (this.currentEcoFee) {
          formData.append("properties[_ecofee]", this.currentEcoFee);
        }
        const dataToChange = await Cart.checkItemExists({
          id,
          quantity
        });
        let result;
        if (dataToChange) {
          result = await Cart.change({
            data: {
              ...dataToChange,
              sections: formData.get("sections"),
              sections_url: formData.get("sections_url")
            },
            action: "add"
          });
        } else {
          result = await Cart.add(formData);
        }
        if (window.theme.routes.pageType == "cart" && !Cart.getError(result)) {
          window.location.reload();
        }
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    // The scan's whole answer for this variant: the contract price in shop currency, or null when
    // none could be confirmed. currentEcoFee still feeds the _ecofee line item property.
    setLivePrice(amount) {
      const isPriced = Number.isFinite(amount);
      if (isPriced) {
        this._price = amount;
        this._hasLivePrice = true;
      }
      this.setState(isPriced ? "price-available" : "price-unavailable");
      this.handleAfterPriceUpdate();
    }
    updateUnitPrice(newUnitPrice) {
      if (!this.unitPriceLabel)
        return;
      if (newUnitPrice) {
        this.unitPriceLabel.textContent = `/ ${newUnitPrice}`;
        this.unitPriceLabel.classList.remove("hidden");
      } else {
        this.unitPriceLabel.classList.add("hidden");
      }
      this.handleAfterPriceUpdate();
    }
    setState(state) {
      switch (state) {
        case "stock-unavailable":
          this.classList.add("stock-unavailable");
          break;
        case "stock-available":
          this.classList.remove("stock-unavailable");
          break;
        case "pickup-unavailable":
          this.classList.add("pickup-unavailable");
          break;
        case "pickup-available":
          this.classList.remove("pickup-unavailable");
          break;
        case "sold-multiples":
          this.classList.add("sold-multiples");
          break;
        case "sold-single":
          this.classList.remove("sold-multiples");
          break;
        case "price-unavailable":
          this.classList.add("price-unavailable");
          break;
        case "price-available":
          this.classList.remove("price-unavailable");
          break;
        case "deleting":
          this.classList.add("is-loading", "is-deleting");
          break;
        case "adding":
          this.classList.add("is-loading", "is-atc-loading");
          break;
        case "reset":
          this.classList.remove("is-loading", "is-atc-loading", "is-deleting");
      }
    }
    // Override this method in child class
    handleAfterPriceUpdate() {
    }
  };

  // app/scripts/theme.js
  (async () => {
    customElements.define("drawer-component", Drawer);
    customElements.define("card-product", CardProduct);
    window.MyLists = new my_lists_default();
    const customerLoggedIn = localStorage.getItem("customer-logged-in");
    document.body.addEventListener("keyup", (e) => {
      e.code == "Escape" && document.querySelectorAll("popup-component.active").forEach((popup) => {
        popup.close();
      });
    });
    if (!theme.editor) {
      section.init();
    }
    theme.cartAttributesChecked = theme.customer.isB2B ? Cart.checkB2BAttributes() : Cart.checkNonB2BAttributes();
    await theme.cartAttributesChecked;
    await Cart.checkLineItemsProperties();
    localStorage.setItem("customer-logged-in", theme.customer.isLoggedIn);
  })();
})();
